import { formatCurrency, formatNumber, type FormatOptions, formatPercent } from "../../../utils/formatNumber";
import { metricColors } from "../../../components/custom/analytics/palettes";
import { metrics as dspMetrics } from "../../dsp/utils/metrics";
import type { TSelector } from "../../../i18n/types";
import type { ReactNode } from "react";

const IMPRESSIONS_AXIS = 2;
const CLICK_AXIS = 3;
const UNITS_AXIS = 4;
const SERVING_HOURS_AXIS = 5;
const SALES_AXIS = 6;
const PERCENT_AXIS = 7;
const ROI_AXIS = 8;
const BSR_AXIS = 9;
const PRICE_AXIS = 10;
const RATINGS_AXIS = 11;
const RATINGS_TOTAL_AXIS = 12;
const SCORE_AXIS = 13;
const BID_AXIS = 14;
const RATE_AXIS = 15;
const PER_PAGE_VIEW = 16;
const DAYS_AXIS = 17;
const PURCHASES_AXIS = 18;
const CART_ADDS_AXIS = 19;
const VOLUME_AXIS = 20;

// A user-facing label, always an i18n selector function.
export type MetricLabel = TSelector;

/**
 * Selector for a metric's display name, resolved through the `metrics` namespace
 * at `byId.<id>.name`. The `byId` table lives in `src/locales/en/metrics.json`
 * and is the source of truth — registry definitions no longer carry `name`.
 */
export function metricNameSelector(id: string): TSelector {
  return ($) => $.metrics.byId[id].name;
}

/** Selector for a metric's tooltip text. Mirrors {@link metricNameSelector}. */
export function metricToolTipSelector(id: string): TSelector {
  return ($) => $.metrics.byId[id].toolTip;
}

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
type MetricAccessor<T = any> = (row: T | undefined) => number | null;

function goalMetricDeltaAccessor<T>(cur: MetricAccessor<T>, prev: MetricAccessor<T>): MetricAccessor<T> {
  return (d) => {
    const c = cur(d);
    const p = prev(d);
    return c == null || p == null ? null : c - p;
  };
}

function goalMetricPercentAccessor<T>(cur: MetricAccessor<T>, prev: MetricAccessor<T>): MetricAccessor<T> {
  return (d) => {
    const c = cur(d);
    const p = prev(d);
    if (c == null || p == null || p === 0) return null;
    return (c - p) / p;
  };
}

export type CompareHighlight = "neutral" | "positiveIsGood" | "negativeIsGood";
// oxlint-disable-next-line @typescript-eslint/no-explicit-any
interface BaseMetricConfig<T = any> {
  /** This is what identifies the metric in various states, e.g are we sorting by this column, are we showing this line on the graph etc. */
  id: string;
  /** The field name used by the server API (for sorting/filtering). Defaults to `id` if not set. */
  serverId?: string;
  /**
   * Optional display label for inline metrics. Registered metrics omit this and
   * resolve via {@link metricNameSelector} (byId lookup). Inline definitions can
   * set a selector or a pre-resolved string.
   */
  name?: MetricLabel | string;
  /** Optional tooltip for inline metrics. See {@link name}. */
  toolTip?: MetricLabel | string;
  /** Color of the line in the graph */
  color?: string;
  accessor: MetricAccessor<T>;
  totalOverrideAccessor?: string;
  format: "date" | "currency" | "percent" | "number" | "text" | "percent_goal" | "currency_goal";
  /**
   * For goal-format metrics, returns coverage info so MetricCell can render the
   * "N / M days with goals" hint. Return null when coverage is fully met or unknown.
   */
  goal_coverage_accessor?: (row: T | undefined) => { withGoal: number; total: number } | null;
  /** @default 2 */
  decimalPlaces?: number;
  chartType?: "line" | "column";
  incremental?: boolean;
  compact?: boolean;
  axis?: number;
  lower?: boolean;
  sortNullsLast?: boolean;

  /**
   * Determines color of the "compare" part of the metric
   * @default "positiveIsGood"
   */
  compareHighlight?: CompareHighlight;
  alternateSortingOptions?: { label: MetricLabel; value: string }[];
  /** For column selector */
  grouping?: string;
}

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
type MetricWithCompare<T = any> = BaseMetricConfig<T> & {
  defaultCompare: "prev" | "delta" | "percent";
  prev_accessor: MetricAccessor<T>;
  percent_accessor: MetricAccessor<T>;
  delta_accessor: MetricAccessor<T>;
};

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
type MetricWithoutCompare<T = any> = BaseMetricConfig<T> & {
  defaultCompare?: undefined;
  prev_accessor?: undefined;
  percent_accessor?: undefined;
  delta_accessor?: undefined;
};

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export type MetricConfig<T = any> = MetricWithCompare<T> | MetricWithoutCompare<T>;

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export type MetricObject<T = any> = MetricConfig<T> & {
  overrideValue?: number | null;
  actual?: number | null;
  target?: number | null;
  targetFormatter?: (value: unknown) => ReactNode;
  targetLabel?: MetricLabel;
  options?: MetricObject<T>;
  formatter: (v: unknown) => ReactNode;
  value: unknown;
  marketPlace?: string;
  targetColor?: string;
  compareFormatOptions?: FormatOptions;
};

const metrics: MetricConfig[] = [
  {
    id: "enroll_date",
    color: metricColors.sales[0],
    accessor: (d) => d?.enroll_date,
    format: "date",
    axis: SALES_AXIS,
    grouping: "configuration",
  },
  {
    id: "sales",
    defaultCompare: "percent",
    color: metricColors.sales[0],
    accessor: (d) => d?.total_sales || 0,
    prev_accessor: (d) => d?.total_sales_prev || 0,
    percent_accessor: (d) => d?.total_sales_percent || 0,
    delta_accessor: (d) => d?.total_sales_delta || 0,
    format: "currency",
    axis: SALES_AXIS,
    grouping: "merchandising",
    decimalPlaces: 2,
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "total_sales_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "total_sales_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "total_sales_delta",
      },
    ],
  },
  {
    id: "sales_keyword",
    defaultCompare: "percent",
    color: metricColors.sales[0],
    accessor: (d) => d?.sales_keyword || 0,
    prev_accessor: (d) => d?.sales_keyword_prev || 0,
    percent_accessor: (d) => d?.sales_keyword_percent || 0,
    delta_accessor: (d) => d?.sales_keyword_delta || 0,
    format: "currency",
    axis: SALES_AXIS,
    grouping: "advertising",
    decimalPlaces: 2,
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "sales_keyword_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "sales_keyword_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "sales_keyword_delta",
      },
    ],
  },
  {
    id: "inc_cashflow",
    defaultCompare: "prev",
    color: metricColors.sales[0],
    accessor: (d) => d?.inc_cashflow || 0,
    prev_accessor: (d) => d?.inc_cashflow_prev || 0,
    percent_accessor: (d) => d?.inc_cashflow_percent || 0,
    delta_accessor: (d) => d?.inc_cashflow_delta || 0,
    format: "currency",
    axis: SALES_AXIS,
    chartType: "column",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "inc_cashflow_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "inc_cashflow_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "inc_cashflow_delta",
      },
    ],
  },
  {
    id: "inc_cashflow_prev",
    color: metricColors.sales[0],
    accessor: (d) => d?.inc_cashflow_prev,
    format: "currency",
    axis: SALES_AXIS,
    chartType: "column",
  },
  {
    id: "inc_units",
    defaultCompare: "prev",
    color: metricColors.sales[0],
    accessor: (d) => d?.inc_units || 0,
    prev_accessor: (d) => d?.inc_units_prev || 0,
    percent_accessor: (d) => d?.inc_units_percent || 0,
    delta_accessor: (d) => d?.inc_units_delta || 0,
    format: "number",
    axis: UNITS_AXIS,
    chartType: "column",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "inc_units_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "inc_units_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "inc_units_delta",
      },
    ],
  },
  {
    id: "inc_units_prev",
    color: metricColors.sales[0],
    accessor: (d) => d?.inc_units_prev,
    format: "number",
    axis: UNITS_AXIS,
    chartType: "column",
  },
  {
    id: "inc_contribution",
    defaultCompare: "prev",
    color: metricColors.sales[0],
    accessor: (d) => d?.inc_contribution || 0,
    prev_accessor: (d) => d?.inc_contribution_prev || 0,
    percent_accessor: (d) => d?.inc_contribution_percent || 0,
    delta_accessor: (d) => d?.inc_contribution_delta || 0,
    format: "currency",
    axis: SALES_AXIS,
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "inc_contribution_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "inc_contribution_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "inc_contribution_delta",
      },
    ],
  },
  {
    id: "inc_contribution_prev",
    color: metricColors.sales[0],
    accessor: (d) => d?.inc_contribution_prev,
    format: "currency",
    axis: SALES_AXIS,
  },

  {
    id: "target_sales",
    defaultCompare: "prev",
    color: metricColors.sales[0],
    accessor: (d) => d?.target_sales,
    prev_accessor: (d) => d?.target_sales_prev,
    percent_accessor: (d) => d?.target_sales_percent,
    delta_accessor: (d) => d?.target_sales_delta,
    format: "currency",
    axis: SALES_AXIS,
    grouping: "configuration",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "target_sales_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "target_sales_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "target_sales_delta",
      },
    ],
  },
  {
    id: "target_sales_prev",
    color: metricColors.sales[0],
    accessor: (d) => d?.target_sales_prev,
    format: "currency",
    axis: SALES_AXIS,
  },
  {
    id: "sales_percent",
    color: metricColors.sales[0],
    accessor: (d) => d?.total_sales_percent,
    format: "percent",
    incremental: true,
    axis: PERCENT_AXIS,
  },
  {
    id: "sales_delta",
    color: metricColors.sales[0],
    accessor: (d) => d?.total_sales_delta,
    format: "currency",
    incremental: true,
    axis: SALES_AXIS,
  },
  {
    id: "sales_expcectations",
    color: metricColors.sales[0],
    accessor: (d) => d?.sales_expcectations,
    format: "currency",
    axis: SALES_AXIS,
    grouping: "merchandising",
  },
  {
    id: "sales_expectations_percent",
    color: metricColors.sales[0],
    accessor: (d) => d?.sales_expcectations_percent,
    format: "percent",
    incremental: true,
    axis: PERCENT_AXIS,
  },
  {
    id: "cost",
    defaultCompare: "percent",
    color: metricColors.cost[0],
    accessor: (d) => d?.total_cost,
    prev_accessor: (d) => d?.total_cost_prev,
    percent_accessor: (d) => d?.total_cost_percent,
    delta_accessor: (d) => d?.total_cost_delta,
    format: "currency",
    axis: SALES_AXIS,
    grouping: "advertising",
    compareHighlight: "neutral",
    decimalPlaces: 2,
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "total_cost_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "total_cost_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "total_cost_delta",
      },
    ],
  },
  {
    id: "cost_percent",
    color: metricColors.cost[0],
    accessor: (d) => d?.total_cost_percent,
    format: "percent",
    incremental: true,
    axis: PERCENT_AXIS,
  },
  {
    id: "roms",
    defaultCompare: "delta",
    color: metricColors.roms[0],
    accessor: (d) => d?.roms,
    prev_accessor: (d) => d?.roms_prev,
    percent_accessor: (d) => d?.roms_percent,
    delta_accessor: (d) => d?.roms_delta,
    format: "number",
    chartType: "line",
    decimalPlaces: 2,
    axis: ROI_AXIS,
    compareHighlight: "neutral",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "roms_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "roms_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "roms_delta",
      },
    ],
  },
  {
    id: "roms_delta",
    color: metricColors.roms[0],
    accessor: (d) => d?.roms_delta,
    format: "number",
    incremental: true,
    chartType: "line",
    decimalPlaces: 2,
    axis: ROI_AXIS,
  },
  {
    id: "units",
    defaultCompare: "percent",
    color: metricColors.units[0],
    accessor: (d) => d?.total_units || 0,
    prev_accessor: (d) => d?.total_units_prev || 0,
    percent_accessor: (d) => d?.total_units_percent || 0,
    delta_accessor: (d) => d?.total_units_delta || 0,
    format: "number",
    decimalPlaces: 0,
    axis: UNITS_AXIS,
    chartType: "column",
    grouping: "merchandising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "total_units_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "total_units_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "total_units_delta",
      },
    ],
  },
  {
    id: "units_keyword",
    defaultCompare: "percent",
    color: metricColors.units[0],
    accessor: (d) => d?.attributed_units_ordered_7d_keyword || 0,
    prev_accessor: (d) => d?.attributed_units_ordered_7d_keyword_prev || 0,
    percent_accessor: (d) => d?.attributed_units_ordered_7d_keyword_percent || 0,
    delta_accessor: (d) => d?.attributed_units_ordered_7d_keyword_delta || 0,
    format: "number",
    decimalPlaces: 0,
    axis: UNITS_AXIS,
    chartType: "column",
    grouping: "advertising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "attributed_units_ordered_7d_keyword_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "attributed_units_ordered_7d_keyword_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "attributed_units_ordered_7d_keyword_delta",
      },
    ],
  },
  {
    id: "target_units",
    defaultCompare: "prev",
    color: undefined,
    accessor: (d) => d?.target_units,
    prev_accessor: (d) => d?.target_units_prev,
    percent_accessor: (d) => d?.target_units_percent,
    delta_accessor: (d) => d?.target_units_delta,
    format: "number",
    axis: UNITS_AXIS,
    chartType: "column",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "target_units_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "target_units_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "target_units_delta",
      },
    ],
  },
  {
    id: "target_units_prev",
    color: undefined,
    accessor: (d) => d?.target_units_prev,
    format: "number",
    axis: UNITS_AXIS,
    chartType: "column",
  },
  {
    id: "total_orders",
    defaultCompare: "percent",
    color: metricColors.units[0],
    accessor: (d) => d?.total_order_count,
    prev_accessor: (d) => d?.total_order_count_prev,
    percent_accessor: (d) => d?.total_order_count_percent,
    delta_accessor: (d) => d?.total_order_count_delta,
    format: "number",
    axis: UNITS_AXIS,
    chartType: "column",
    grouping: "merchandising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "total_order_count_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "total_order_count_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "total_order_count_delta",
      },
    ],
  },
  {
    id: "total_order_count_percent",
    color: metricColors.units[0],
    accessor: (d) => d?.total_order_count_percent,
    format: "percent",
    axis: PERCENT_AXIS,
    incremental: true,
  },
  {
    id: "units_expectations",
    color: metricColors.units[0],
    accessor: (d) => d?.units_expectations,
    format: "number",
    axis: UNITS_AXIS,
    chartType: "column",
    grouping: "configuration",
  },
  {
    id: "units_percent",
    color: metricColors.units[0],
    accessor: (d) => d?.total_units_percent,
    format: "percent",
    axis: PERCENT_AXIS,
    incremental: true,
  },
  {
    id: "units_delta",
    color: metricColors.units[0],
    accessor: (d) => d?.total_units_delta,
    format: "number",
    axis: PERCENT_AXIS,
    incremental: true,
  },
  {
    id: "units_expectations_percent",
    color: metricColors.units[0],
    accessor: (d) => d?.units_expectations_percent,
    format: "percent",
    axis: PERCENT_AXIS,
    incremental: true,
  },
  {
    id: "margin",
    defaultCompare: "delta",
    color: metricColors.margin[0],
    accessor: (d) => d?.margin,
    prev_accessor: (d) => d?.margin_prev,
    percent_accessor: (d) => d?.margin_percent,
    delta_accessor: (d) => d?.margin_delta,
    format: "percent",
    chartType: "line",
    axis: PERCENT_AXIS,
    grouping: "calculated",
    decimalPlaces: 1,
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "margin_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "margin_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "margin_delta",
      },
    ],
  },
  {
    id: "margin_delta",
    color: metricColors.margin[0],
    accessor: (d) => d?.margin_delta,
    format: "percent",
    incremental: true,
    chartType: "line",
    axis: PERCENT_AXIS,
    decimalPlaces: 1,
  },
  {
    id: "margin_wo_adspend",
    defaultCompare: "delta",
    color: metricColors.margin[0],
    accessor: (d) => d?.margin_wo_adspend,
    prev_accessor: (d) => d?.margin_wo_adspend_prev,
    percent_accessor: (d) => d?.margin_wo_adspend_percent,
    delta_accessor: (d) => d?.margin_wo_adspend_delta,
    format: "percent",
    chartType: "line",
    axis: PERCENT_AXIS,
    grouping: "calculated",
    decimalPlaces: 1,
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "margin_wo_adspend_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "margin_wo_adspend_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "margin_wo_adspend_delta",
      },
    ],
  },
  {
    id: "target_margin",
    color: metricColors.margin[0],
    accessor: (d) => d?.target_margin,
    format: "percent",
    chartType: "line",
    axis: PERCENT_AXIS,
    grouping: "configuration",
  },
  {
    id: "margin_wo_adspend_delta",
    color: metricColors.margin[0],
    accessor: (d) => d?.margin_wo_adspend_delta,
    format: "percent",
    incremental: true,
    chartType: "line",
    axis: PERCENT_AXIS,
    decimalPlaces: 1,
  },
  {
    id: "margin_expcectations",
    color: metricColors.sales[0],
    accessor: (d) => d?.margin_expcectations,
    format: "percent",
    axis: SALES_AXIS,
    grouping: "configuration",
  },
  {
    id: "margin_expectations_percent",
    color: metricColors.sales[0],
    accessor: (d) => d?.margin_expcectations_percent,
    format: "percent",
    incremental: true,
    axis: PERCENT_AXIS,
  },
  {
    id: "ad_sales",
    defaultCompare: "percent",
    color: metricColors.sales[4],
    accessor: (d) => d?.ad_sales ?? 0,
    prev_accessor: (d) => d?.ad_sales_prev ?? 0,
    percent_accessor: (d) => d?.ad_sales_percent ?? 0,
    delta_accessor: (d) => d?.ad_sales_delta ?? 0,
    format: "currency",
    axis: SALES_AXIS,
    grouping: "advertising",
    decimalPlaces: 2,
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "ad_sales_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "ad_sales_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "ad_sales_delta",
      },
    ],
  },
  {
    id: "ad_sales_percent",
    color: metricColors.sales[4],
    accessor: (d) => d?.ad_sales_percent,
    format: "percent",
    axis: PERCENT_AXIS,
    incremental: true,
  },
  {
    id: "ad_sales_delta",
    color: metricColors.sales[4],
    accessor: (d) => d?.ad_sales_delta,
    format: "currency",
    axis: SALES_AXIS,
    incremental: true,
  },
  {
    id: "ad_sales_same_sku",
    defaultCompare: "percent",
    color: metricColors.sales[4],
    accessor: (d) => d?.ad_sales_same_sku ?? 0,
    prev_accessor: (d) => d?.ad_sales_same_sku_prev ?? 0,
    percent_accessor: (d) => d?.ad_sales_same_sku_percent ?? 0,
    delta_accessor: (d) => d?.ad_sales_same_sku_delta ?? 0,
    format: "currency",
    axis: SALES_AXIS,
    grouping: "advertising",
    decimalPlaces: 2,
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "ad_sales_same_sku_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "ad_sales_same_sku_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "ad_sales_same_sku_delta",
      },
    ],
  },
  {
    id: "ad_sales_same_sku_percent",
    color: metricColors.sales[4],
    accessor: (d) => d?.ad_sales_same_sku_percent,
    format: "percent",
    axis: PERCENT_AXIS,
    incremental: true,
  },
  {
    id: "ad_spend",
    defaultCompare: "percent",
    color: metricColors.cost[1],
    accessor: (d) => d?.cost ?? 0,
    prev_accessor: (d) => d?.cost_prev ?? 0,
    percent_accessor: (d) => d?.cost_percent ?? 0,
    delta_accessor: (d) => d?.cost_delta ?? 0,
    format: "currency",
    axis: SALES_AXIS,
    grouping: "advertising",
    compareHighlight: "neutral",
    decimalPlaces: 2,
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "cost_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "cost_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "cost_delta",
      },
    ],
  },
  {
    id: "cost_keyword",
    defaultCompare: "percent",
    color: metricColors.cost[1],
    accessor: (d) => d?.cost_keyword ?? 0,
    prev_accessor: (d) => d?.cost_keyword_prev ?? 0,
    percent_accessor: (d) => d?.cost_keyword_percent ?? 0,
    delta_accessor: (d) => d?.cost_keyword_delta ?? 0,
    format: "currency",
    axis: SALES_AXIS,
    grouping: "advertising",
    compareHighlight: "neutral",
    decimalPlaces: 2,
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "cost_keyword_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "cost_keyword_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "cost_keyword_delta",
      },
    ],
  },
  {
    id: "ad_spend_percent",
    color: metricColors.cost[1],
    accessor: (d) => d?.cost_percent,
    format: "percent",
    axis: PERCENT_AXIS,
    incremental: true,
  },
  {
    id: "spend_delta",
    color: metricColors.cost[1],
    accessor: (d) => d?.spend_delta,
    format: "currency",
    axis: SALES_AXIS,
    incremental: true,
  },
  {
    id: "clicks",
    defaultCompare: "percent",
    color: metricColors.metric1[0],
    accessor: (d) => d?.clicks ?? 0,
    prev_accessor: (d) => d?.clicks_prev ?? 0,
    percent_accessor: (d) => d?.clicks_percent ?? 0,
    delta_accessor: (d) => d?.clicks_delta ?? 0,
    chartType: "column",
    decimalPlaces: 0,
    format: "number",
    axis: CLICK_AXIS,
    grouping: "merchandising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "clicks_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "clicks_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "clicks_delta",
      },
    ],
  },
  {
    id: "clicks_keyword",
    defaultCompare: "percent",
    color: metricColors.metric1[0],
    accessor: (d) => d?.clicks_keyword ?? 0,
    prev_accessor: (d) => d?.clicks_keyword_prev ?? 0,
    percent_accessor: (d) => d?.clicks_keyword_percent ?? 0,
    delta_accessor: (d) => d?.clicks_keyword_delta ?? 0,
    chartType: "column",
    decimalPlaces: 0,
    format: "number",
    axis: CLICK_AXIS,
    grouping: "advertising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "clicks_keyword_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "clicks_keyword_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "clicks_keyword_delta",
      },
    ],
  },
  {
    id: "clicks_percent",
    color: metricColors.metric1[0],
    accessor: (d) => d?.clicks_percent,
    format: "percent",
    axis: PERCENT_AXIS,
    incremental: true,
  },
  {
    id: "troas",
    defaultCompare: "delta",
    color: metricColors.roms[3],
    accessor: (d) => d?.total_roas ?? 0,
    prev_accessor: (d) => d?.total_roas_prev ?? 0,
    percent_accessor: (d) => d?.total_roas_percent ?? 0,
    delta_accessor: (d) => d?.total_roas_delta ?? 0,
    format: "number",
    decimalPlaces: 1,
    chartType: "line",
    axis: ROI_AXIS,
    grouping: "merchandising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "total_roas_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "total_roas_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "total_roas_delta",
      },
    ],
  },
  {
    id: "troas_delta",
    color: metricColors.roms[3],
    accessor: (d) => d?.total_roas_delta,
    format: "number",
    decimalPlaces: 1,
    chartType: "line",
    axis: ROI_AXIS,
    incremental: true,
  },
  {
    id: "roas",
    defaultCompare: "delta",
    color: metricColors.roms[1],
    accessor: (d) => d?.roas ?? 0,
    prev_accessor: (d) => d?.roas_prev ?? 0,
    percent_accessor: (d) => d?.roas_percent ?? 0,
    delta_accessor: (d) => d?.roas_delta ?? 0,
    format: "number",
    decimalPlaces: 1,
    chartType: "line",
    axis: ROI_AXIS,
    grouping: "advertising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "roas_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "roas_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "roas_delta",
      },
    ],
  },
  {
    id: "roas_delta",
    color: metricColors.roms[1],
    accessor: (d) => d?.roas_delta,
    format: "number",
    decimalPlaces: 1,
    chartType: "line",
    axis: ROI_AXIS,
    incremental: true,
  },
  {
    id: "roas_same_sku",
    defaultCompare: "delta",
    color: metricColors.roms[1],
    accessor: (d) => d?.roas_same_sku ?? 0,
    prev_accessor: (d) => d?.roas_same_sku_prev ?? 0,
    percent_accessor: (d) => d?.roas_same_sku_percent ?? 0,
    delta_accessor: (d) => d?.roas_same_sku_delta ?? 0,
    format: "number",
    decimalPlaces: 1,
    chartType: "line",
    axis: ROI_AXIS,
    grouping: "advertising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "roas_same_sku_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "roas_same_sku_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "roas_same_sku_delta",
      },
    ],
  },
  {
    id: "roas_same_sku_delta",
    color: metricColors.roms[1],
    accessor: (d) => d?.roas_same_sku_delta,
    format: "number",
    decimalPlaces: 1,
    chartType: "line",
    axis: ROI_AXIS,
    incremental: true,
  },
  {
    id: "total_acos",
    defaultCompare: "delta",
    color: metricColors.acos[1],
    accessor: (d) => d?.total_acos ?? 0,
    prev_accessor: (d) => d?.total_acos_prev ?? 0,
    percent_accessor: (d) => d?.total_acos_percent ?? 0,
    delta_accessor: (d) => d?.total_acos_delta ?? 0,
    format: "percent",
    chartType: "line",
    axis: PERCENT_AXIS,
    grouping: "merchandising",
    lower: true,
    compareHighlight: "negativeIsGood",
    decimalPlaces: 1,
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "total_acos_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "total_acos_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "total_acos_delta",
      },
    ],
  },
  {
    id: "total_acos_delta",
    color: metricColors.acos[1],
    accessor: (d) => d?.total_acos_delta,
    format: "percent",
    decimalPlaces: 1,
    chartType: "line",
    axis: PERCENT_AXIS,
    incremental: true,
    lower: true,
  },
  {
    id: "acos",
    defaultCompare: "delta",
    color: metricColors.acos[3],
    accessor: (d) => d?.acos ?? 0,
    prev_accessor: (d) => d?.acos_prev ?? 0,
    percent_accessor: (d) => d?.acos_percent ?? 0,
    delta_accessor: (d) => d?.acos_delta ?? 0,
    format: "percent",
    chartType: "line",
    axis: PERCENT_AXIS,
    grouping: "advertising",
    lower: true,
    compareHighlight: "negativeIsGood",
    decimalPlaces: 1,
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "acos_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "acos_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "acos_delta",
      },
    ],
  },
  {
    id: "acos_keyword",
    defaultCompare: "delta",
    color: metricColors.acos[3],
    accessor: (d) => d?.acos_keyword ?? 0,
    prev_accessor: (d) => d?.acos_keyword_prev ?? 0,
    percent_accessor: (d) => d?.acos_keyword_percent ?? 0,
    delta_accessor: (d) => d?.acos_keyword_delta ?? 0,
    format: "percent",
    chartType: "line",
    axis: PERCENT_AXIS,
    grouping: "advertising",
    lower: true,
    compareHighlight: "negativeIsGood",
    decimalPlaces: 1,
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "acos_keyword_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "acos_keyword_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "acos_keyword_delta",
      },
    ],
  },
  {
    id: "acos_delta",
    color: metricColors.acos[3],
    accessor: (d) => d?.acos_delta,
    format: "percent",
    decimalPlaces: 1,
    chartType: "line",
    axis: PERCENT_AXIS,
    incremental: true,
    lower: true,
  },
  {
    id: "acos_same_sku",
    defaultCompare: "delta",
    color: undefined,
    accessor: (d) => d?.acos_same_sku ?? 0,
    prev_accessor: (d) => d?.acos_same_sku_prev ?? 0,
    percent_accessor: (d) => d?.acos_same_sku_percent ?? 0,
    delta_accessor: (d) => d?.acos_same_sku_delta ?? 0,
    format: "percent",
    chartType: "line",
    axis: PERCENT_AXIS,
    grouping: "advertising",
    lower: true,
    compareHighlight: "negativeIsGood",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "acos_same_sku_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "acos_same_sku_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "acos_same_sku_delta",
      },
    ],
  },
  {
    id: "acos_same_sku_delta",
    color: undefined,
    accessor: (d) => d?.acos_same_sku_delta,
    format: "percent",
    decimalPlaces: 1,
    chartType: "line",
    axis: PERCENT_AXIS,
    incremental: true,
    lower: true,
  },
  {
    id: "profit",
    defaultCompare: "percent",
    color: metricColors.metric1[0],
    accessor: (d) => d?.profit,
    prev_accessor: (d) => d?.profit_prev,
    percent_accessor: (d) => d?.profit_percent,
    delta_accessor: (d) => d?.profit_delta,
    format: "currency",
    axis: SALES_AXIS,
    grouping: "calculated",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "profit_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "profit_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "profit_delta",
      },
    ],
  },
  {
    id: "profit_percent",
    color: metricColors.metric1[0],
    accessor: (d) => d?.profit_percent,
    format: "percent",
    axis: PERCENT_AXIS,
    incremental: true,
  },
  {
    id: "profit_delta",
    color: metricColors.metric1[0],
    accessor: (d) => d?.profit_delta,
    format: "currency",
    axis: SALES_AXIS,
    incremental: true,
  },
  {
    id: "profit_wo_adspend",
    defaultCompare: "percent",
    color: metricColors.metric1[0],
    accessor: (d) => d?.profit_wo_adspend,
    prev_accessor: (d) => d?.profit_wo_adspend_prev,
    percent_accessor: (d) => d?.profit_wo_adspend_percent,
    delta_accessor: (d) => d?.profit_wo_adspend_delta,
    format: "currency",
    axis: SALES_AXIS,
    grouping: "calculated",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "profit_wo_adspend_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "profit_wo_adspend_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "profit_wo_adspend_delta",
      },
    ],
  },
  {
    id: "profit_wo_adspend_percent",
    color: metricColors.metric1[0],
    accessor: (d) => d?.profit_wo_adspend_percent,
    format: "percent",
    axis: PERCENT_AXIS,
    incremental: true,
  },
  {
    id: "profit_wo_adspend_delta",
    color: metricColors.metric1[0],
    accessor: (d) => d?.profit_wo_adspend_delta,
    format: "currency",
    axis: SALES_AXIS,
    incremental: true,
  },

  {
    id: "profit_expcectations",
    color: metricColors.sales[0],
    accessor: (d) => d?.profit_expcectations,
    format: "currency",
    axis: SALES_AXIS,
    grouping: "calculated",
  },
  {
    id: "profit_expectations_percent",
    color: metricColors.sales[0],
    accessor: (d) => d?.profit_expcectations_percent,
    format: "percent",
    incremental: true,
    axis: PERCENT_AXIS,
  },
  {
    id: "price",
    color: metricColors.metric2[0],
    accessor: (d) => d?.product_price,
    format: "currency",
    chartType: "line",
    axis: PRICE_AXIS,
    grouping: "configuration",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "product_price_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "product_price_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "product_price_delta",
      },
    ],
  },
  {
    id: "reference_price",
    color: metricColors.metric2[0],
    accessor: (d) => d?.reference_price,
    format: "currency",
    chartType: "line",
    axis: PRICE_AXIS,
    grouping: "calculated",
  },
  {
    id: "min_price",
    color: metricColors.metric2[0],
    accessor: (d) => d?.min_price,
    format: "currency",
    chartType: "line",
    axis: PRICE_AXIS,
    grouping: "configuration",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "min_price_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "min_price_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "min_price_delta",
      },
    ],
  },
  {
    id: "max_price",
    color: metricColors.metric2[0],
    accessor: (d) => d?.max_price,
    format: "currency",
    chartType: "line",
    axis: PRICE_AXIS,
    grouping: "configuration",
  },
  {
    id: "suggested_price",
    color: metricColors.metric2[0],
    accessor: (d) => d?.suggested_price,
    format: "currency",
    chartType: "line",
    axis: PRICE_AXIS,
    grouping: "configuration",
  },
  {
    id: "product_price_prev",
    color: metricColors.metric2[0],
    accessor: (d) => d?.product_price_prev,
    format: "currency",
    chartType: "line",
    axis: PRICE_AXIS,
    grouping: "configuration",
  },
  {
    id: "total_conv_rate",
    defaultCompare: "delta",
    color: metricColors.metric1[0],
    accessor: (d) => d?.total_conv_rate,
    prev_accessor: (d) => d?.total_conv_rate_prev,
    percent_accessor: (d) => d?.total_conv_rate_percent,
    delta_accessor: (d) => d?.total_conv_rate_delta,
    format: "percent",
    chartType: "line",
    axis: PERCENT_AXIS,
    grouping: "merchandising",
    decimalPlaces: 2,
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "total_conv_rate_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "total_conv_rate_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "total_conv_rate_delta",
      },
    ],
  },
  {
    id: "total_conv_rate_delta",
    color: metricColors.metric1[0],
    accessor: (d) => d?.total_conv_rate_delta,
    format: "percent",
    chartType: "line",
    incremental: true,
    axis: PERCENT_AXIS,
  },
  {
    id: "total_conv_rate_prev",
    color: metricColors.metric1[0],
    accessor: (d) => d?.total_conv_rate_prev,
    format: "percent",
  },
  {
    id: "organic_sales",
    defaultCompare: "percent",
    color: metricColors.sales[0],
    accessor: (d) => d?.organic_sales,
    prev_accessor: (d) => d?.organic_sales_prev,
    percent_accessor: (d) => d?.organic_sales_percent,
    delta_accessor: (d) => d?.organic_sales_delta,
    axis: SALES_AXIS,
    format: "currency",
    grouping: "calculated",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "organic_sales_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "organic_sales_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "organic_sales_delta",
      },
    ],
  },
  {
    id: "organic_sales_delta",
    color: metricColors.metric1[0],
    accessor: (d) => d?.organic_sales_delta,
    format: "percent",
  },
  {
    id: "organic_sales_prev",
    color: metricColors.metric1[0],
    accessor: (d) => d?.organic_sales_prev,
    format: "currency",
  },
  {
    id: "organic_sales_percent",
    color: metricColors.metric1[0],
    accessor: (d) => d?.organic_sales_percent,
    format: "percent",
  },
  {
    id: "organic_units",
    defaultCompare: "percent",
    color: metricColors.units[0],
    accessor: (d) => d?.organic_units,
    prev_accessor: (d) => d?.organic_units_prev,
    percent_accessor: (d) => d?.organic_units_percent,
    delta_accessor: (d) => d?.organic_units_delta,
    format: "number",
    axis: UNITS_AXIS,
    chartType: "column",
    grouping: "calculated",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "organic_units_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "organic_units_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "organic_units_delta",
      },
    ],
  },
  {
    id: "organic_units_delta",
    color: metricColors.metric1[0],
    accessor: (d) => d?.organic_unit_delta,
    format: "percent",
  },
  {
    id: "organic_units_percent",
    color: metricColors.metric1[0],
    accessor: (d) => d?.organic_units_percent,
    format: "percent",
  },
  {
    id: "organic_units_prev",
    color: metricColors.metric1[0],
    accessor: (d) => d?.organic_units_prev,
    format: "number",
  },
  {
    id: "organic_page_views",
    defaultCompare: "percent",
    color: metricColors.metric2[0],
    accessor: (d) => d?.organic_page_views,
    prev_accessor: (d) => d?.organic_page_views_prev,
    percent_accessor: (d) => d?.organic_page_views_percent,
    delta_accessor: (d) => d?.organic_page_views_delta,
    format: "number",
    grouping: "calculated",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "organic_page_views_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "organic_page_views_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "organic_page_views_delta",
      },
    ],
  },
  {
    id: "organic_page_views_delta",
    color: metricColors.metric1[0],
    accessor: (d) => d?.organic_page_views_delta,
    format: "percent",
  },
  {
    id: "organic_page_views_prev",
    color: metricColors.metric1[0],
    accessor: (d) => d?.organic_page_views_prev,
    format: "number",
  },
  {
    id: "organic_page_views_percent",
    color: metricColors.metric1[0],
    accessor: (d) => d?.organic_page_views_percent,
    format: "percent",
  },
  {
    id: "org_conv_rate",
    defaultCompare: "delta",
    color: metricColors.metric1[0],
    accessor: (d) => d?.org_conv_rate,
    prev_accessor: (d) => d?.org_conv_rate_prev,
    percent_accessor: (d) => d?.org_conv_rate_percent,
    delta_accessor: (d) => d?.org_conv_rate_delta,
    format: "percent",
    grouping: "calculated",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "org_conv_rate_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "org_conv_rate_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "org_conv_rate_delta",
      },
    ],
  },
  {
    id: "org_conv_rate_delta",
    color: metricColors.metric1[0],
    accessor: (d) => d?.org_conv_rate_delta,
    format: "percent",
    chartType: "line",
    incremental: true,
    axis: PERCENT_AXIS,
    decimalPlaces: 2,
  },
  {
    id: "org_conv_rate_prev",
    color: metricColors.metric1[0],
    accessor: (d) => d?.org_conv_rate_prev,
    format: "percent",
  },
  {
    id: "price_delta",
    color: metricColors.metric2[0],
    accessor: (d) => d?.product_price_delta,
    format: "currency",
    chartType: "line",
    axis: PRICE_AXIS,
    incremental: true,
  },
  {
    id: "bsr",
    defaultCompare: "percent",
    grouping: "merchandising",
    color: metricColors.metric3[0],
    accessor: (d) => d?.bsr_rank,
    prev_accessor: (d) => d?.bsr_rank_prev,
    percent_accessor: (d) => d?.bsr_rank_percent,
    delta_accessor: (d) => d?.bsr_rank_delta,
    totalOverrideAccessor: "last_bsr_rank",
    format: "number",
    chartType: "line",
    axis: BSR_AXIS,
    lower: true,
    sortNullsLast: true,
    compareHighlight: "negativeIsGood",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "bsr_rank_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "bsr_rank_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "bsr_rank_delta",
      },
    ],
  },
  {
    id: "bsr_percent",
    color: metricColors.metric3[0],
    accessor: (d) => d?.bsr_rank_percent,
    format: "percent",
    axis: PERCENT_AXIS,
    incremental: true,
    lower: true,
  },
  {
    id: "bsr_delta",
    color: metricColors.metric3[0],
    accessor: (d) => d?.bsr_rank_delta,
    format: "number",
    axis: BSR_AXIS,
    incremental: true,
    lower: true,
  },
  {
    id: "promo_sales",
    defaultCompare: "percent",
    grouping: "merchandising",
    color: metricColors.sales[2],
    accessor: (d) => d?.promo_sales,
    prev_accessor: (d) => d?.promo_sales_prev,
    percent_accessor: (d) => d?.promo_sales_percent,
    delta_accessor: (d) => d?.promo_sales_delta,
    format: "currency",
    axis: SALES_AXIS,
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "promo_sales_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "promo_sales_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "promo_sales_delta",
      },
    ],
  },
  {
    id: "promo_sales_percent",
    color: metricColors.sales[2],
    accessor: (d) => d?.promo_sales_percent,
    axis: PERCENT_AXIS,
    format: "percent",
    chartType: "line",
    incremental: true,
  },
  {
    id: "promo_units",
    defaultCompare: "percent",
    grouping: "merchandising",
    color: metricColors.units[1],
    accessor: (d) => d?.promo_units,
    prev_accessor: (d) => d?.promo_units_prev,
    percent_accessor: (d) => d?.promo_units_percent,
    delta_accessor: (d) => d?.promo_units_delta,
    format: "number",
    axis: UNITS_AXIS,
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "promo_units_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "promo_units_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "promo_units_delta",
      },
    ],
  },
  {
    id: "promo_units_percent",
    color: metricColors.units[1],
    accessor: (d) => d?.promo_units_percent,
    format: "percent",
    axis: PERCENT_AXIS,
    chartType: "line",
    incremental: true,
  },
  {
    id: "rods",
    defaultCompare: "delta",
    grouping: "merchandising",
    color: metricColors.roms[2],
    accessor: (d) => d?.rods,
    prev_accessor: (d) => d?.rods_prev,
    percent_accessor: (d) => d?.rods_percent,
    delta_accessor: (d) => d?.rods_delta,
    axis: ROI_AXIS,
    format: "number",
    chartType: "line",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "rods_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "rods_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "rods_delta",
      },
    ],
  },
  {
    id: "rods_delta",
    color: metricColors.roms[2],
    accessor: (d) => d?.rods_delta,
    format: "number",
    axis: ROI_AXIS,
    incremental: true,
  },
  {
    id: "promo_conversion_rate",
    defaultCompare: "delta",
    grouping: "merchandising",
    color: metricColors.metric1[2],
    accessor: (d) => d?.conv,
    prev_accessor: (d) => d?.conv_prev,
    percent_accessor: (d) => d?.conv_percent,
    delta_accessor: (d) => d?.conv_delta,
    axis: PERCENT_AXIS,
    format: "percent",
    chartType: "line",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "conv_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "conv_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "conv_delta",
      },
    ],
  },
  {
    id: "promo_conversion_rate_delta",
    color: metricColors.metric1[2],
    accessor: (d) => d?.conv_delta,
    axis: PERCENT_AXIS,
    format: "percent",
    chartType: "line",
  },
  {
    id: "promo_cost",
    defaultCompare: "percent",
    grouping: "merchandising",
    color: metricColors.cost[2],
    accessor: (d) => d?.promo_cost,
    prev_accessor: (d) => d?.promo_cost_prev,
    percent_accessor: (d) => d?.promo_cost_percent,
    delta_accessor: (d) => d?.promo_cost_delta,
    format: "currency",
    axis: SALES_AXIS,
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "promo_cost_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "promo_cost_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "promo_cost_delta",
      },
    ],
  },
  {
    id: "promo_cost_percent",
    color: metricColors.cost[2],
    accessor: (d) => d?.promo_cost_percent,
    format: "percent",
    axis: PERCENT_AXIS,
    incremental: true,
  },
  {
    id: "promo_glance_views",
    defaultCompare: "percent",
    grouping: "merchandising",
    color: metricColors.metric1[0],
    accessor: (d) => d?.glance_views,
    prev_accessor: (d) => d?.glance_views_prev,
    percent_accessor: (d) => d?.glance_views_percent,
    delta_accessor: (d) => d?.glance_views_delta,
    format: "number",
    axis: UNITS_AXIS,
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "glance_views_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "glance_views_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "glance_views_delta",
      },
    ],
  },
  {
    id: "promo_conv_rate",
    defaultCompare: "delta",
    grouping: "merchandising",
    color: metricColors.metric1[2],
    accessor: (d) => d?.conv,
    prev_accessor: (d) => d?.conv_prev,
    percent_accessor: (d) => d?.conv_percent,
    delta_accessor: (d) => d?.conv_delta,
    axis: PERCENT_AXIS,
    format: "percent",
    chartType: "line",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "conv_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "conv_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "conv_delta",
      },
    ],
  },
  {
    id: "cogs",
    defaultCompare: "percent",
    grouping: "calculated",
    color: metricColors.metric3[0],
    accessor: (d) => d?.total_cogs,
    prev_accessor: (d) => d?.total_cogs_prev,
    percent_accessor: (d) => d?.total_cogs_percent,
    delta_accessor: (d) => d?.total_cogs_delta,
    format: "currency",
    axis: SALES_AXIS,
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "total_cogs_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "total_cogs_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "total_cogs_delta",
      },
    ],
  },
  {
    id: "cogs_percent",
    color: metricColors.metric3[0],
    accessor: (d) => d?.total_cogs_percent,
    format: "percent",
    axis: PERCENT_AXIS,
    incremental: true,
  },
  {
    id: "last_bsr_rank",
    grouping: "merchandising",
    color: metricColors.metric3[0],
    accessor: (d) => d?.last_bsr_rank,
    format: "number",
    chartType: "line",
    axis: BSR_AXIS,
    compareHighlight: "negativeIsGood",
  },
  {
    id: "last_bsr_rank_delta",
    color: metricColors.metric3[0],
    accessor: (d) => d?.last_bsr_rank_delta,
    format: "number",
    chartType: "line",
    axis: BSR_AXIS,
  },
  {
    id: "rating",
    defaultCompare: "percent",
    grouping: "merchandising",
    color: metricColors.metric4[0],
    accessor: (d) => d?.rating,
    prev_accessor: (d) => d?.rating_prev,
    percent_accessor: (d) => d?.rating_percent,
    delta_accessor: (d) => d?.rating_delta,
    totalOverrideAccessor: "last_rating",
    format: "number",
    chartType: "line",
    axis: RATINGS_AXIS,
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "rating_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "rating_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "rating_delta",
      },
    ],
  },
  {
    id: "last_rating",
    grouping: "merchandising",
    color: metricColors.metric4[0],
    accessor: (d) => d?.last_rating,
    format: "number",
    chartType: "line",
    axis: RATINGS_AXIS,
  },
  {
    id: "rating_percent",
    color: metricColors.metric4[0],
    accessor: (d) => d?.last_rating_percent,
    format: "percent",
    incremental: true,
    axis: PERCENT_AXIS,
  },
  {
    id: "ratings_total",
    defaultCompare: "percent",
    grouping: "merchandising",
    color: metricColors.metric4[4],
    accessor: (d) => d?.ratings_total,
    prev_accessor: (d) => d?.ratings_total_prev,
    percent_accessor: (d) => d?.ratings_total_percent,
    delta_accessor: (d) => d?.ratings_total_delta,
    totalOverrideAccessor: "last_ratings_total",
    format: "number",
    chartType: "line",
    axis: RATINGS_TOTAL_AXIS,
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "ratings_total_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "ratings_total_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "ratings_total_delta",
      },
    ],
  },
  {
    id: "last_ratings_total",
    grouping: "merchandising",
    color: metricColors.metric4[0],
    accessor: (d) => d?.last_ratings_total,
    format: "number",
    chartType: "line",
    axis: RATINGS_TOTAL_AXIS,
  },
  {
    id: "ratings_total_percent",
    color: metricColors.metric4[0],
    accessor: (d) => d?.ratings_total_percent,
    format: "percent",
    incremental: true,
    axis: PERCENT_AXIS,
  },
  {
    id: "seo_score",
    defaultCompare: "percent",
    color: metricColors.sales[4],
    accessor: (d) => d?.seo_score,
    prev_accessor: (d) => d?.seo_score_prev,
    percent_accessor: (d) => d?.seo_score_percent,
    delta_accessor: (d) => d?.seo_score_delta,
    format: "number",
    axis: SCORE_AXIS,
    decimalPlaces: 0,
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "seo_score_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "seo_score_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "seo_score_delta",
      },
    ],
  },
  {
    id: "seo_score_percent",
    color: metricColors.sales[4],
    accessor: (d) => d?.seo_score_percent,
    format: "percent",
    incremental: true,
    chartType: "line",
    axis: PERCENT_AXIS,
  },
  {
    id: "buyability_score",
    defaultCompare: "percent",
    color: metricColors.metric1[4],
    decimalPlaces: 0,
    accessor: (d) => d?.buyability_score,
    prev_accessor: (d) => d?.buyability_score_prev,
    percent_accessor: (d) => d?.buyability_score_percent,
    delta_accessor: (d) => d?.buyability_score_delta,
    format: "number",
    axis: SCORE_AXIS,
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "buyability_score_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "buyability_score_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "buyability_score_delta",
      },
    ],
  },
  {
    id: "buyability_score_percent",
    color: metricColors.metric1[4],
    accessor: (d) => d?.buyability_score_percent,
    format: "percent",
    incremental: true,
    chartType: "line",
    axis: PERCENT_AXIS,
  },
  {
    id: "conversion_rate",
    defaultCompare: "delta",
    color: metricColors.metric2[4],
    accessor: (d) => d?.conv_rate ?? 0,
    prev_accessor: (d) => d?.conv_rate_prev ?? 0,
    percent_accessor: (d) => d?.conv_rate_percent ?? 0,
    delta_accessor: (d) => d?.conv_rate_delta ?? 0,
    format: "percent",
    chartType: "line",
    axis: RATE_AXIS,
    grouping: "advertising",
    decimalPlaces: 2,
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "conv_rate_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "conv_rate_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "conv_rate_delta",
      },
    ],
  },
  {
    id: "conversion_rate_keyword",
    defaultCompare: "delta",
    color: metricColors.metric2[4],
    accessor: (d) => d?.cr_keyword ?? 0,
    prev_accessor: (d) => d?.cr_keyword_prev ?? 0,
    percent_accessor: (d) => d?.cr_keyword_percent ?? 0,
    delta_accessor: (d) => d?.cr_keyword_delta ?? 0,
    format: "percent",
    chartType: "line",
    axis: RATE_AXIS,
    grouping: "advertising",
    decimalPlaces: 2,
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "cr_keyword_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "cr_keyword_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "cr_keyword_delta",
      },
    ],
  },
  {
    id: "conversion_rate_delta",
    color: metricColors.metric2[4],
    accessor: (d) => d?.conv_rate_delta,
    format: "percent",
    chartType: "line",
    incremental: true,
    axis: PERCENT_AXIS,
    decimalPlaces: 2,
  },
  {
    id: "ctr",
    defaultCompare: "delta",
    color: metricColors.metric3[4],
    accessor: (d) => d?.click_thru_rate,
    prev_accessor: (d) => d?.click_thru_rate_prev,
    percent_accessor: (d) => d?.click_thru_rate_percent,
    delta_accessor: (d) => d?.click_thru_rate_delta,
    format: "percent",
    chartType: "line",
    axis: RATE_AXIS,
    grouping: "advertising",
    decimalPlaces: 2,
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "click_thru_rate_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "click_thru_rate_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "click_thru_rate_delta",
      },
    ],
  },
  {
    id: "ctr_delta",
    color: metricColors.metric3[4],
    accessor: (d) => d?.click_thru_rate_delta,
    format: "percent",
    chartType: "line",
    incremental: true,
    axis: PERCENT_AXIS,
    decimalPlaces: 2,
  },
  {
    id: "cpc",
    defaultCompare: "delta",
    color: metricColors.metric4[4],
    accessor: (d) => d?.cost_per_click ?? 0,
    prev_accessor: (d) => d?.cost_per_click_prev ?? 0,
    percent_accessor: (d) => d?.cost_per_click_percent ?? 0,
    delta_accessor: (d) => d?.cost_per_click_delta ?? 0,
    format: "currency",
    chartType: "line",
    axis: PRICE_AXIS,
    grouping: "advertising",
    decimalPlaces: 2,
    lower: true,
    compareHighlight: "negativeIsGood",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "cost_per_click_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "cost_per_click_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "cost_per_click_delta",
      },
    ],
  },
  {
    id: "cpc_keyword",
    defaultCompare: "delta",
    color: metricColors.metric4[4],
    accessor: (d) => d?.cpc_keyword ?? 0,
    prev_accessor: (d) => d?.cpc_keyword_prev ?? 0,
    percent_accessor: (d) => d?.cpc_keyword_percent ?? 0,
    delta_accessor: (d) => d?.cpc_keyword_delta ?? 0,
    format: "currency",
    chartType: "line",
    axis: PRICE_AXIS,
    grouping: "advertising",
    decimalPlaces: 2,
    lower: true,
    compareHighlight: "negativeIsGood",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "cpc_keyword_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "cpc_keyword_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "cpc_keyword_delta",
      },
    ],
  },
  {
    id: "cpc_delta",
    color: metricColors.metric4[4],
    accessor: (d) => d?.cost_per_click_delta,
    format: "percent",
    chartType: "line",
    incremental: true,
    axis: PERCENT_AXIS,
    decimalPlaces: 2,
  },
  {
    id: "impressions",
    defaultCompare: "percent",
    color: metricColors.metric1[3],
    accessor: (d) => d?.impressions ?? 0,
    prev_accessor: (d) => d?.impressions_prev ?? 0,
    percent_accessor: (d) => d?.impressions_percent ?? 0,
    delta_accessor: (d) => d?.impressions_delta ?? 0,
    format: "number",
    decimalPlaces: 0,
    chartType: "column",
    axis: IMPRESSIONS_AXIS,
    grouping: "merchandising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "impressions_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "impressions_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "impressions_delta",
      },
    ],
  },
  {
    id: "viewable_impressions",
    defaultCompare: "percent",
    color: metricColors.metric1[3],
    accessor: (d) => d?.viewable_impressions ?? 0,
    prev_accessor: (d) => d?.viewable_impressions_prev ?? 0,
    percent_accessor: (d) => d?.viewable_impressions_percent ?? 0,
    delta_accessor: (d) => d?.viewable_impressions_delta ?? 0,
    format: "number",
    chartType: "line",
    axis: IMPRESSIONS_AXIS,
    grouping: "advertising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "viewable_impressions_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "viewable_impressions_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "viewable_impressions_delta",
      },
    ],
  },
  {
    id: "viewable_impressions_percent",
    color: metricColors.metric1[3],
    accessor: (d) => d?.viewable_impressions_percent ?? 0,
    format: "percent",
    incremental: true,
    axis: PERCENT_AXIS,
  },
  {
    id: "viewability_rate",
    defaultCompare: "delta",
    color: metricColors.metric1[3],
    accessor: (d) => d?.viewability_rate ?? 0,
    prev_accessor: (d) => d?.viewability_rate_prev ?? 0,
    percent_accessor: (d) => d?.viewability_rate_percent ?? 0,
    delta_accessor: (d) => d?.viewability_rate_delta ?? 0,
    format: "percent",
    decimalPlaces: 2,
    axis: PERCENT_AXIS,
    chartType: "line",
    grouping: "merchandising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "viewability_rate_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "viewability_rate_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "viewability_rate_delta",
      },
    ],
  },
  {
    id: "viewability_rate_delta",
    color: metricColors.metric1[3],
    accessor: (d) => d?.viewability_rate_delta ?? 0,
    format: "percent",
    decimalPlaces: 2,
    incremental: true,
    chartType: "line",
    axis: PERCENT_AXIS,
  },
  {
    id: "view_click_through_rate",
    defaultCompare: "delta",
    color: metricColors.metric1[3],
    accessor: (d) => d?.view_click_through_rate ?? 0,
    prev_accessor: (d) => d?.view_click_through_rate_prev ?? 0,
    percent_accessor: (d) => d?.view_click_through_rate_percent ?? 0,
    delta_accessor: (d) => d?.view_click_through_rate_delta ?? 0,
    format: "percent",
    decimalPlaces: 2,
    axis: PERCENT_AXIS,
    chartType: "line",
    grouping: "advertising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "view_click_through_rate_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "view_click_through_rate_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "view_click_through_rate_delta",
      },
    ],
  },
  {
    id: "view_click_through_rate_delta",
    color: metricColors.metric1[3],
    accessor: (d) => d?.view_click_through_rate_delta ?? 0,
    format: "percent",
    decimalPlaces: 2,
    incremental: true,
    chartType: "line",
    axis: PERCENT_AXIS,
  },
  {
    id: "impressions_percent",
    color: metricColors.metric1[3],
    accessor: (d) => d?.impressions_percent,
    format: "percent",
    incremental: true,
    axis: PERCENT_AXIS,
  },
  {
    id: "asp",
    color: metricColors.metric2[0],
    accessor: (d) => (d?.total_units > 0 ? d?.total_sales / d?.total_units : 0),
    // prev_accessor: (d) => (d?.total_units_prev > 0 ? d?.total_sales_prev / d?.total_units_prev : 0),
    format: "currency",
    axis: PRICE_AXIS,
  },
  {
    id: "inc_price",
    color: metricColors.metric2[0],
    accessor: (d) => d?.inc_price,
    format: "currency",
    axis: PRICE_AXIS,
  },

  {
    id: "page_views",
    defaultCompare: "percent",
    color: metricColors.metric2[0],
    accessor: (d) => d?.page_views,
    prev_accessor: (d) => d?.page_views_prev,
    percent_accessor: (d) => d?.page_views_percent,
    delta_accessor: (d) => d?.page_views_delta,
    format: "number",
    axis: IMPRESSIONS_AXIS,
    grouping: "merchandising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "page_views_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "page_views_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "page_views_delta",
      },
    ],
  },
  {
    id: "page_views_percent",
    color: metricColors.metric2[1],
    accessor: (d) => d?.page_views_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "match_type",
    color: metricColors.metric2[0],
    accessor: (d) => d?.match_type,
    format: "text",
    axis: IMPRESSIONS_AXIS,
    grouping: "configuration",
  },
  {
    id: "bid",
    color: metricColors.sales[0],
    accessor: (d) => d?.bid,
    format: "currency",
    axis: BID_AXIS,
    decimalPlaces: 2,
    grouping: "configuration",
  },
  {
    id: "daily_bid",
    color: metricColors.metric2[0],
    accessor: (d) => d?.daily_bid ?? 0,
    format: "currency",
    axis: PRICE_AXIS,
    decimalPlaces: 2,
    grouping: "configuration",
  },
  {
    id: "target_source",
    color: metricColors.metric4[0],
    accessor: (d) => d?.target_source,
    chartType: "column",
    format: "text",
    axis: CLICK_AXIS,
    grouping: "configuration",
  },
  {
    id: "orders",
    defaultCompare: "percent",
    color: metricColors.metric4[4],
    accessor: (d) => d?.conversion ?? 0,
    prev_accessor: (d) => d?.conversion_prev ?? 0,
    percent_accessor: (d) => d?.conversion_percent ?? 0,
    delta_accessor: (d) => d?.conversion_delta ?? 0,
    chartType: "column",
    decimalPlaces: 0,
    format: "number",
    axis: UNITS_AXIS,
    grouping: "advertising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "conversion_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "conversion_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "conversion_delta",
      },
    ],
  },
  {
    id: "conversions_keyword",
    defaultCompare: "percent",
    color: metricColors.metric4[4],
    accessor: (d) => d?.conversions_keyword ?? 0,
    prev_accessor: (d) => d?.conversions_keyword_prev ?? 0,
    percent_accessor: (d) => d?.conversions_keyword_percent ?? 0,
    delta_accessor: (d) => d?.conversions_keyword_delta ?? 0,
    chartType: "column",
    decimalPlaces: 0,
    format: "number",
    axis: UNITS_AXIS,
    grouping: "advertising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "conversions_keyword_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "conversions_keyword_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "conversions_keyword_delta",
      },
    ],
  },
  {
    id: "conversion_percent",
    color: metricColors.metric4[4],
    accessor: (d) => d?.conversion_percent ?? 0,
    chartType: "column",
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "orders_percent",
    color: metricColors.metric4[4],
    accessor: (d) => d?.orders_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "orders_same_sku",
    defaultCompare: "percent",
    color: metricColors.metric4[4],
    accessor: (d) => d?.conversion_same_sku,
    prev_accessor: (d) => d?.conversion_same_sku_prev,
    percent_accessor: (d) => d?.conversion_same_sku_percent,
    delta_accessor: (d) => d?.conversion_same_sku_delta,
    chartType: "column",
    format: "number",
    axis: UNITS_AXIS,
    grouping: "advertising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "conversion_same_sku_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "conversion_same_sku_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "conversion_same_sku_delta",
      },
    ],
  },
  {
    id: "orders_same_sku_percent",
    color: metricColors.metric4[4],
    accessor: (d) => d?.orders_same_sku_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "user_status",
    color: metricColors.metric4[0],
    accessor: (d) => d?.user_status,
    format: "text",
    axis: PERCENT_AXIS,
  },
  {
    id: "stock_status",
    color: metricColors.metric4[0],
    accessor: (d) => d?.stock_status,
    format: "text",
    axis: PERCENT_AXIS,
  },
  {
    id: "serving_status",
    color: metricColors.metric4[0],
    accessor: (d) => d?.serving_status,
    format: "text",
    axis: PERCENT_AXIS,
  },
  {
    id: "profit_per_page_view",
    defaultCompare: "percent",
    color: metricColors.metric4[0],
    accessor: (d) => d?.profit_per_page_view,
    prev_accessor: (d) => d?.profit_per_page_view_prev,
    percent_accessor: (d) => d?.profit_per_page_view_percent,
    delta_accessor: (d) => d?.profit_per_page_view_delta,
    format: "currency",
    axis: SALES_AXIS,
    grouping: "merchandising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "profit_per_page_view_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "profit_per_page_view_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "profit_per_page_view_delta",
      },
    ],
  },
  {
    id: "profit_per_page_view_delta",
    color: metricColors.metric4[0],
    accessor: (d) => d?.profit_per_page_view_delta,
    format: "currency",
    axis: PER_PAGE_VIEW,
  },
  {
    id: "profit_per_page_view_percent",
    color: metricColors.metric4[0],
    accessor: (d) => d?.profit_per_page_view_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "profit_per_page_view_wo_adspend",
    defaultCompare: "percent",
    color: metricColors.metric4[0],
    accessor: (d) => d?.profit_per_page_view_wo_adspend,
    prev_accessor: (d) => d?.profit_per_page_view_wo_adspend_prev,
    percent_accessor: (d) => d?.profit_per_page_view_wo_adspend_percent,
    delta_accessor: (d) => d?.profit_per_page_view_wo_adspend_delta,
    format: "currency",
    axis: SALES_AXIS,
    grouping: "merchandising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "profit_per_page_view_wo_adspend_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "profit_per_page_view_wo_adspend_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "profit_per_page_view_wo_adspend_delta",
      },
    ],
  },
  {
    id: "profit_per_page_view_wo_adspend_delta",
    color: metricColors.metric4[0],
    accessor: (d) => d?.profit_per_page_view_wo_adspend_delta,
    format: "currency",
    axis: PER_PAGE_VIEW,
  },
  {
    id: "profit_per_page_view_wo_adspend_percent",
    color: metricColors.metric4[0],
    accessor: (d) => d?.profit_per_page_view_wo_adspend_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "boosted_profit",
    color: metricColors.metric4[0],
    accessor: (d) => d?.boosted_profit,
    format: "currency",
    axis: PRICE_AXIS,
  },
  {
    id: "serving_hours",
    defaultCompare: "delta",
    color: metricColors.metric2[0],
    accessor: (d) => d?.serving_hours,
    prev_accessor: (d) => d?.serving_hours_prev ?? 0,
    percent_accessor: (d) => d?.serving_hours_percent ?? 0,
    delta_accessor: (d) => d?.serving_hours_delta ?? 0,
    format: "number",
    axis: SERVING_HOURS_AXIS,
    chartType: "column",
    grouping: "advertising",
    decimalPlaces: 0,
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "serving_hours_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "serving_hours_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "serving_hours_delta",
      },
    ],
  },
  {
    id: "serving_hours_delta",
    color: metricColors.metric2[1],
    accessor: (d) => d?.serving_hours_delta,
    format: "number",
    axis: SERVING_HOURS_AXIS,
    decimalPlaces: 0,
  },
  {
    id: "budget_goal_progression",
    color: metricColors.sales[4],
    accessor: (d) => d?.budget_goal_progression ?? 0,
    format: "currency",
    axis: SALES_AXIS,
  },
  {
    id: "active_ad_type_count",
    color: metricColors.metric2[1],
    accessor: (d) => d?.active_ad_type_count,
    format: "number",
    axis: SERVING_HOURS_AXIS,
  },
  {
    id: "active_ad_type_count_percent",
    color: metricColors.sales[4],
    accessor: (d) => d?.active_ad_type_count_percent ?? 0,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "orders_repeat",
    color: metricColors.repeat[1],
    accessor: (d) => d?.orders_repeat,
    format: "number",
    chartType: "column",
    axis: UNITS_AXIS,
  },
  {
    id: "orders_ntb",
    color: metricColors.ntb[1],
    accessor: (d) => d?.orders_ntb,
    format: "number",
    chartType: "column",
    axis: UNITS_AXIS,
  },
  {
    id: "sales_repeat",
    color: metricColors.repeat[0],
    accessor: (d) => d?.sales_repeat,
    format: "currency",
    axis: SALES_AXIS,
  },
  {
    id: "sales_ntb",
    color: metricColors.ntb[0],
    accessor: (d) => d?.sales_ntb,
    format: "currency",
    axis: SALES_AXIS,
  },
  {
    id: "customer_value",
    color: metricColors.sales[4],
    accessor: (d) => d?.customer_value,
    format: "currency",
    axis: PRICE_AXIS,
  },
  {
    id: "brand_customer_value",
    color: metricColors.sales[3],
    accessor: (d) => d?.brand_customer_value,
    format: "currency",
    axis: PRICE_AXIS,
  },
  {
    id: "cac",
    color: metricColors.cost[1],
    accessor: (d) => d?.cac,
    format: "currency",
    axis: PRICE_AXIS,
  },
  {
    id: "ltv_cac",
    color: metricColors.sales[4],
    accessor: (d) => d?.ltv_cac,
    format: "number",
    axis: ROI_AXIS,
  },
  {
    id: "purchase_freq",
    color: metricColors.metric1[1],
    accessor: (d) => d?.purchase_freq,
    format: "number",
    axis: DAYS_AXIS,
  },
  {
    id: "halo_rank",
    color: metricColors.metric1[1],
    accessor: (d) => d?.halo_rank,
    format: "number",
    axis: BSR_AXIS,
    sortNullsLast: true,
  },
  {
    id: "halo_sales",
    color: metricColors.sales[2],
    accessor: (d) => d?.halo_sales,
    format: "currency",
    axis: SALES_AXIS,
  },
  {
    id: "gateway_rank",
    color: metricColors.metric2[1],
    accessor: (d) => d?.gateway_rank,
    format: "number",
    axis: BSR_AXIS,
    sortNullsLast: true,
  },
  {
    id: "search_query_score",
    defaultCompare: "prev",
    color: metricColors.metric2[1],
    accessor: (d) => d?.search_query_score,
    prev_accessor: (d) => d?.search_query_score_prev,
    percent_accessor: (d) => d?.search_query_score_percent,
    delta_accessor: (d) => d?.search_query_score_delta,
    format: "number",
    axis: SCORE_AXIS,
    grouping: "merchandising",
    decimalPlaces: 1,
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "search_query_score_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "search_query_score_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "search_query_score_delta",
      },
    ],
  },
  {
    id: "search_query_score_prev",
    color: metricColors.metric2[2],
    accessor: (d) => d?.search_query_score_prev,
    format: "number",
    axis: SCORE_AXIS,
    decimalPlaces: 1,
  },
  {
    id: "search_query_volume",
    defaultCompare: "percent",
    color: metricColors.metric2[1],
    accessor: (d) => d?.search_query_volume,
    prev_accessor: (d) => d?.search_query_volume_prev,
    percent_accessor: (d) => d?.search_query_volume_percent,
    delta_accessor: (d) => d?.search_query_volume_delta,
    format: "number",
    axis: VOLUME_AXIS,
    grouping: "merchandising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "search_query_volume_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "search_query_volume_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "search_query_volume_delta",
      },
    ],
  },
  {
    id: "search_query_volume_percent",
    color: metricColors.metric2[1],
    accessor: (d) => d?.search_query_volume_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "impressions_brand_or_asin",
    defaultCompare: "delta",
    color: metricColors.metric1[1],
    accessor: (d) => d?.impressions_brand_or_asin,
    prev_accessor: (d) => d?.impressions_brand_or_asin_prev,
    percent_accessor: (d) => d?.impressions_brand_or_asin_percent,
    delta_accessor: (d) => d?.impressions_brand_or_asin_delta,
    format: "number",
    chartType: "column",
    axis: IMPRESSIONS_AXIS,
    grouping: "calculated",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "impressions_brand_or_asin_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "impressions_brand_or_asin_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "impressions_brand_or_asin_delta",
      },
    ],
  },
  {
    id: "impressions_total",
    defaultCompare: "percent",
    color: metricColors.metric1[3],
    accessor: (d) => d?.impressions_total,
    prev_accessor: (d) => d?.impressions_total_prev,
    percent_accessor: (d) => d?.impressions_total_percent,
    delta_accessor: (d) => d?.impressions_total_delta,
    format: "number",
    chartType: "column",
    axis: IMPRESSIONS_AXIS,
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "impressions_total_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "impressions_total_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "impressions_total_delta",
      },
    ],
  },
  {
    id: "impressions_total_percent",
    color: metricColors.metric1[1],
    accessor: (d) => d?.impressions_total_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "impressions_brand_or_asin_percent",
    color: metricColors.metric1[1],
    accessor: (d) => d?.impressions_brand_or_asin_percent,
    format: "percent",
    axis: PERCENT_AXIS,
    decimalPlaces: 2,
  },
  {
    id: "impressions_brand_or_asin_share",
    defaultCompare: "delta",
    color: metricColors.metric1[1],
    accessor: (d) => d?.impressions_brand_or_asin_share,
    prev_accessor: (d) => d?.impressions_brand_or_asin_share_prev,
    percent_accessor: (d) => d?.impressions_brand_or_asin_share_percent,
    delta_accessor: (d) => d?.impressions_brand_or_asin_share_delta,
    format: "percent",
    axis: PERCENT_AXIS,
    decimalPlaces: 2,
    grouping: "advertising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "impressions_brand_or_asin_share_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "impressions_brand_or_asin_share_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "impressions_brand_or_asin_share_delta",
      },
    ],
  },
  {
    id: "impressions_brand_or_asin_share_delta",
    color: metricColors.metric1[1],
    accessor: (d) => d?.impressions_brand_or_asin_share_delta,
    format: "percent",
    decimalPlaces: 2,
    axis: PERCENT_AXIS,
  },
  {
    id: "clicks_total",
    defaultCompare: "percent",
    color: metricColors.metric2[1],
    accessor: (d) => d?.clicks_total,
    prev_accessor: (d) => d?.clicks_total_prev,
    percent_accessor: (d) => d?.clicks_total_percent,
    delta_accessor: (d) => d?.clicks_total_delta,
    format: "number",
    chartType: "column",
    axis: CLICK_AXIS,
    grouping: "merchandising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "clicks_total_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "clicks_total_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "clicks_total_delta",
      },
    ],
  },
  {
    id: "clicks_total_percent",
    color: metricColors.metric2[1],
    accessor: (d) => d?.clicks_total_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "clicks_total_rate",
    defaultCompare: "delta",
    color: metricColors.metric2[1],
    accessor: (d) => d?.clicks_total_rate,
    prev_accessor: (d) => d?.clicks_total_rate_prev,
    percent_accessor: (d) => d?.clicks_total_rate_percent,
    delta_accessor: (d) => d?.clicks_total_rate_delta,
    format: "percent",
    axis: PERCENT_AXIS,
    grouping: "merchandising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "clicks_total_rate_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "clicks_total_rate_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "clicks_total_rate_delta",
      },
    ],
  },
  {
    id: "clicks_total_rate_delta",
    color: metricColors.metric2[1],
    accessor: (d) => d?.clicks_total_rate_delta,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "clicks_brand_or_asin",
    defaultCompare: "percent",
    color: metricColors.metric2[3],
    accessor: (d) => d?.clicks_brand_or_asin,
    prev_accessor: (d) => d?.clicks_brand_or_asin_prev,
    percent_accessor: (d) => d?.clicks_brand_or_asin_percent,
    delta_accessor: (d) => d?.clicks_brand_or_asin_delta,
    format: "number",
    chartType: "column",
    axis: CLICK_AXIS,
    grouping: "merchandising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "clicks_brand_or_asin_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "clicks_brand_or_asin_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "clicks_brand_or_asin_delta",
      },
    ],
  },
  {
    id: "clicks_brand_or_asin_percent",
    color: metricColors.metric2[1],
    accessor: (d) => d?.clicks_brand_or_asin_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "clicks_brand_or_asin_share",
    defaultCompare: "delta",
    color: metricColors.metric2[1],
    accessor: (d) => d?.clicks_brand_or_asin_share,
    prev_accessor: (d) => d?.clicks_brand_or_asin_share_prev,
    percent_accessor: (d) => d?.clicks_brand_or_asin_share_percent,
    delta_accessor: (d) => d?.clicks_brand_or_asin_share_delta,
    format: "percent",
    axis: PERCENT_AXIS,
    decimalPlaces: 2,
    grouping: "merchandising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "clicks_brand_or_asin_share_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "clicks_brand_or_asin_share_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "clicks_brand_or_asin_share_delta",
      },
    ],
  },
  {
    id: "clicks_brand_or_asin_share_delta",
    color: metricColors.metric2[1],
    accessor: (d) => d?.clicks_brand_or_asin_share_delta,
    format: "percent",
    axis: PERCENT_AXIS,
    decimalPlaces: 2,
  },
  {
    id: "clicks_price_median",
    defaultCompare: "percent",
    color: metricColors.metric2[1],
    accessor: (d) => d?.clicks_price_median,
    prev_accessor: (d) => d?.clicks_price_median_prev,
    percent_accessor: (d) => d?.clicks_price_median_percent,
    delta_accessor: (d) => d?.clicks_price_median_delta,
    format: "currency",
    axis: PRICE_AXIS,
    grouping: "merchandising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "clicks_price_median_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "clicks_price_median_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "clicks_price_median_delta",
      },
    ],
  },
  {
    id: "clicks_price_median_percent",
    color: metricColors.metric2[1],
    accessor: (d) => d?.clicks_price_median_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "clicks_brand_or_asin_price_median",
    defaultCompare: "percent",
    color: metricColors.metric2[1],
    accessor: (d) => d?.clicks_brand_or_asin_price_median,
    prev_accessor: (d) => d?.clicks_brand_or_asin_price_median_prev,
    percent_accessor: (d) => d?.clicks_brand_or_asin_price_median_percent,
    delta_accessor: (d) => d?.clicks_brand_or_asin_price_median_delta,
    format: "currency",
    axis: PRICE_AXIS,
    grouping: "merchandising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "clicks_brand_or_asin_price_median_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "clicks_brand_or_asin_price_median_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "clicks_brand_or_asin_price_median_delta",
      },
    ],
  },
  {
    id: "clicks_brand_or_asin_price_median_percent",
    color: metricColors.metric2[1],
    accessor: (d) => d?.clicks_brand_or_asin_price_median_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "clicks_same_day_shipping_speed",
    defaultCompare: "percent",
    color: metricColors.metric2[0],
    accessor: (d) => d?.clicks_same_day_shipping_speed,
    prev_accessor: (d) => d?.clicks_same_day_shipping_speed_prev,
    percent_accessor: (d) => d?.clicks_same_day_shipping_speed_percent,
    delta_accessor: (d) => d?.clicks_same_day_shipping_speed_delta,
    format: "number",
    chartType: "column",
    axis: CLICK_AXIS,
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "clicks_same_day_shipping_speed_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "clicks_same_day_shipping_speed_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "clicks_same_day_shipping_speed_delta",
      },
    ],
    grouping: "merchandising",
  },
  {
    id: "clicks_same_day_shipping_speed_percent",
    color: metricColors.metric2[1],
    accessor: (d) => d?.clicks_same_day_shipping_speed_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "clicks_1d_shipping_speed",
    defaultCompare: "percent",
    color: metricColors.metric2[2],
    accessor: (d) => d?.clicks_1d_shipping_speed,
    prev_accessor: (d) => d?.clicks_1d_shipping_speed_prev,
    percent_accessor: (d) => d?.clicks_1d_shipping_speed_percent,
    delta_accessor: (d) => d?.clicks_1d_shipping_speed_delta,
    format: "number",
    chartType: "column",
    axis: CLICK_AXIS,
    grouping: "merchandising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "clicks_1d_shipping_speed_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "clicks_1d_shipping_speed_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "clicks_1d_shipping_speed_delta",
      },
    ],
  },
  {
    id: "clicks_1d_shipping_speed_percent",
    color: metricColors.metric2[1],
    accessor: (d) => d?.clicks_1d_shipping_speed_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "clicks_2d_shipping_speed",
    defaultCompare: "percent",
    color: metricColors.metric2[4],
    accessor: (d) => d?.clicks_2d_shipping_speed,
    prev_accessor: (d) => d?.clicks_2d_shipping_speed_prev,
    percent_accessor: (d) => d?.clicks_2d_shipping_speed_percent,
    delta_accessor: (d) => d?.clicks_2d_shipping_speed_delta,
    format: "number",
    chartType: "column",
    axis: CLICK_AXIS,
    grouping: "merchandising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "clicks_2d_shipping_speed_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "clicks_2d_shipping_speed_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "clicks_2d_shipping_speed_delta",
      },
    ],
  },
  {
    id: "clicks_2d_shipping_speed_percent",
    color: metricColors.metric2[1],
    accessor: (d) => d?.clicks_2d_shipping_speed_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "cart_adds_total",
    defaultCompare: "percent",
    color: metricColors.metric3[0],
    accessor: (d) => d?.cart_adds_total,
    prev_accessor: (d) => d?.cart_adds_total_prev,
    percent_accessor: (d) => d?.cart_adds_total_percent,
    delta_accessor: (d) => d?.cart_adds_total_delta,
    format: "number",
    chartType: "column",
    axis: CART_ADDS_AXIS,
    grouping: "merchandising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "cart_adds_total_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "cart_adds_total_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "cart_adds_total_delta",
      },
    ],
  },
  {
    id: "cart_adds_total_percent",
    color: metricColors.metric2[1],
    accessor: (d) => d?.cart_adds_total_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "cart_adds_total_rate",
    defaultCompare: "delta",
    color: metricColors.metric2[1],
    accessor: (d) => d?.cart_adds_total_rate,
    prev_accessor: (d) => d?.cart_adds_total_rate_prev,
    percent_accessor: (d) => d?.cart_adds_total_rate_percent,
    delta_accessor: (d) => d?.cart_adds_total_rate_delta,
    format: "percent",
    axis: PERCENT_AXIS,
    decimalPlaces: 2,
    grouping: "merchandising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "cart_adds_total_rate_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "cart_adds_total_rate_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "cart_adds_total_rate_delta",
      },
    ],
  },
  {
    id: "cart_adds_brand_or_asin",
    defaultCompare: "percent",
    color: metricColors.metric3[1],
    accessor: (d) => d?.cart_adds_brand_or_asin,
    prev_accessor: (d) => d?.cart_adds_brand_or_asin_prev,
    percent_accessor: (d) => d?.cart_adds_brand_or_asin_percent,
    delta_accessor: (d) => d?.cart_adds_brand_or_asin_delta,
    format: "number",
    chartType: "column",
    axis: CART_ADDS_AXIS,
    grouping: "merchandising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "cart_adds_brand_or_asin_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "cart_adds_brand_or_asin_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "cart_adds_brand_or_asin_delta",
      },
    ],
  },
  {
    id: "cart_adds_brand_or_asin_percent",
    color: metricColors.metric2[1],
    accessor: (d) => d?.cart_adds_brand_or_asin_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "cart_adds_brand_or_asin_share",
    defaultCompare: "delta",
    color: metricColors.metric2[1],
    accessor: (d) => d?.cart_adds_brand_or_asin_share,
    prev_accessor: (d) => d?.cart_adds_brand_or_asin_share_prev,
    percent_accessor: (d) => d?.cart_adds_brand_or_asin_share_percent,
    delta_accessor: (d) => d?.cart_adds_brand_or_asin_share_delta,
    format: "percent",
    axis: PERCENT_AXIS,
    decimalPlaces: 2,
    grouping: "merchandising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "cart_adds_brand_or_asin_share_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "cart_adds_brand_or_asin_share_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "cart_adds_brand_or_asin_share_delta",
      },
    ],
  },
  {
    id: "cart_adds_brand_or_asin_share_delta",
    color: metricColors.metric2[1],
    accessor: (d) => d?.cart_adds_brand_or_asin_share_delta,
    format: "percent",
    axis: PERCENT_AXIS,
    decimalPlaces: 2,
  },
  {
    id: "cart_adds_price_median",
    defaultCompare: "percent",
    color: metricColors.metric2[1],
    accessor: (d) => d?.cart_adds_price_median,
    prev_accessor: (d) => d?.cart_adds_price_median_prev,
    percent_accessor: (d) => d?.cart_adds_price_median_percent,
    delta_accessor: (d) => d?.cart_adds_price_median_delta,
    format: "currency",
    axis: PRICE_AXIS,
    grouping: "merchandising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "cart_adds_price_median_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "cart_adds_price_median_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "cart_adds_price_median_delta",
      },
    ],
  },
  {
    id: "cart_adds_brand_or_asin_price_median",
    defaultCompare: "percent",
    color: metricColors.metric2[1],
    accessor: (d) => d?.cart_adds_brand_or_asin_price_median,
    prev_accessor: (d) => d?.cart_adds_brand_or_asin_price_median_prev,
    percent_accessor: (d) => d?.cart_adds_brand_or_asin_price_median_percent,
    delta_accessor: (d) => d?.cart_adds_brand_or_asin_price_median_delta,
    format: "currency",
    axis: PRICE_AXIS,
    grouping: "merchandising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "cart_adds_brand_or_asin_price_median_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "cart_adds_brand_or_asin_price_median_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "cart_adds_brand_or_asin_price_median_delta",
      },
    ],
  },
  {
    id: "cart_adds_brand_or_asin_price_median_percent",
    color: metricColors.metric2[1],
    accessor: (d) => d?.cart_adds_brand_or_asin_price_median_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "cart_adds_same_day_shipping_speed",
    defaultCompare: "percent",
    color: metricColors.metric3[2],
    accessor: (d) => d?.cart_adds_same_day_shipping_speed,
    prev_accessor: (d) => d?.cart_adds_same_day_shipping_speed_prev,
    percent_accessor: (d) => d?.cart_adds_same_day_shipping_speed_percent,
    delta_accessor: (d) => d?.cart_adds_same_day_shipping_speed_delta,
    format: "number",
    chartType: "column",
    axis: CART_ADDS_AXIS,
    grouping: "merchandising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "cart_adds_same_day_shipping_speed_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "cart_adds_same_day_shipping_speed_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "cart_adds_same_day_shipping_speed_delta",
      },
    ],
  },
  {
    id: "cart_adds_same_day_shipping_speed_percent",
    color: metricColors.metric2[1],
    accessor: (d) => d?.cart_adds_same_day_shipping_speed_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "cart_adds_1d_shipping_speed",
    defaultCompare: "percent",
    color: metricColors.metric3[3],
    accessor: (d) => d?.cart_adds_1d_shipping_speed,
    prev_accessor: (d) => d?.cart_adds_1d_shipping_speed_prev,
    percent_accessor: (d) => d?.cart_adds_1d_shipping_speed_percent,
    delta_accessor: (d) => d?.cart_adds_1d_shipping_speed_delta,
    format: "number",
    chartType: "column",
    axis: CART_ADDS_AXIS,
    grouping: "merchandising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "cart_adds_1d_shipping_speed_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "cart_adds_1d_shipping_speed_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "cart_adds_1d_shipping_speed_delta",
      },
    ],
  },
  {
    id: "cart_adds_1d_shipping_speed_percent",
    color: metricColors.metric2[1],
    accessor: (d) => d?.cart_adds_1d_shipping_speed_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "cart_adds_2d_shipping_speed",
    defaultCompare: "percent",
    color: metricColors.metric3[4],
    accessor: (d) => d?.cart_adds_2d_shipping_speed,
    prev_accessor: (d) => d?.cart_adds_2d_shipping_speed_prev,
    percent_accessor: (d) => d?.cart_adds_2d_shipping_speed_percent,
    delta_accessor: (d) => d?.cart_adds_2d_shipping_speed_delta,
    format: "number",
    chartType: "column",
    axis: CART_ADDS_AXIS,
    grouping: "merchandising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "cart_adds_2d_shipping_speed_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "cart_adds_2d_shipping_speed_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "cart_adds_2d_shipping_speed_delta",
      },
    ],
  },
  {
    id: "cart_adds_2d_shipping_speed_percent",
    color: metricColors.metric2[1],
    accessor: (d) => d?.cart_adds_2d_shipping_speed_percent,
    format: "percent",
  },
  {
    id: "purchases_total",
    defaultCompare: "percent",
    color: metricColors.metric4[0],
    accessor: (d) => d?.purchases_total,
    prev_accessor: (d) => d?.purchases_total_prev,
    percent_accessor: (d) => d?.purchases_total_percent,
    delta_accessor: (d) => d?.purchases_total_delta,
    format: "number",
    chartType: "column",
    axis: PURCHASES_AXIS,
    grouping: "merchandising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "purchases_total_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "purchases_total_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "purchases_total_delta",
      },
    ],
  },
  {
    id: "purchases_total_percent",
    color: metricColors.metric2[1],
    accessor: (d) => d?.purchases_total_percent,
    format: "number",
    axis: PERCENT_AXIS,
  },
  {
    id: "purchases_total_rate",
    defaultCompare: "delta",
    color: metricColors.metric2[1],
    accessor: (d) => d?.purchases_total_rate,
    prev_accessor: (d) => d?.purchases_total_rate_prev,
    percent_accessor: (d) => d?.purchases_total_rate_percent,
    delta_accessor: (d) => d?.purchases_total_rate_delta,
    format: "percent",
    axis: PERCENT_AXIS,
    grouping: "merchandising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "purchases_total_rate_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "purchases_total_rate_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "purchases_total_rate_delta",
      },
    ],
  },
  {
    id: "purchases_total_rate_delta",
    color: metricColors.metric2[1],
    accessor: (d) => d?.purchases_total_rate_delta,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "purchases_brand_or_asin",
    defaultCompare: "percent",
    color: metricColors.metric4[1],
    accessor: (d) => d?.purchases_brand_or_asin,
    prev_accessor: (d) => d?.purchases_brand_or_asin_prev,
    percent_accessor: (d) => d?.purchases_brand_or_asin_percent,
    delta_accessor: (d) => d?.purchases_brand_or_asin_delta,
    format: "number",
    chartType: "column",
    axis: PURCHASES_AXIS,
    grouping: "merchandising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "purchases_brand_or_asin_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "purchases_brand_or_asin_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "purchases_brand_or_asin_delta",
      },
    ],
  },
  {
    id: "purchases_brand_or_asin_percent",
    color: metricColors.metric2[1],
    accessor: (d) => d?.purchases_brand_or_asin_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "purchases_brand_or_asin_share",
    defaultCompare: "percent",
    color: metricColors.metric2[1],
    accessor: (d) => d?.purchases_brand_or_asin_share,
    prev_accessor: (d) => d?.purchases_brand_or_asin_share_prev,
    percent_accessor: (d) => d?.purchases_brand_or_asin_share_percent,
    delta_accessor: (d) => d?.purchases_brand_or_asin_share_delta,
    format: "percent",
    axis: PERCENT_AXIS,
    grouping: "merchandising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "purchases_brand_or_asin_share_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "purchases_brand_or_asin_share_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "purchases_brand_or_asin_share_delta",
      },
    ],
  },
  {
    id: "purchases_brand_or_asin_share_delta",
    color: metricColors.metric2[1],
    accessor: (d) => d?.purchases_brand_or_asin_share_delta,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "purchases_price_median",
    defaultCompare: "percent",
    color: metricColors.metric2[1],
    accessor: (d) => d?.purchases_price_median,
    prev_accessor: (d) => d?.purchases_price_median_prev,
    percent_accessor: (d) => d?.purchases_price_median_percent,
    delta_accessor: (d) => d?.purchases_price_median_delta,
    format: "currency",
    axis: PRICE_AXIS,
    grouping: "merchandising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "purchases_price_median_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "purchases_price_median_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "purchases_price_median_delta",
      },
    ],
  },
  {
    id: "purchases_price_median_percent",
    color: metricColors.metric2[1],
    accessor: (d) => d?.purchases_price_median_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "purchases_brand_or_asin_price_median",
    defaultCompare: "percent",
    color: metricColors.metric2[1],
    accessor: (d) => d?.purchases_brand_or_asin_price_median,
    prev_accessor: (d) => d?.purchases_brand_or_asin_price_median_prev,
    percent_accessor: (d) => d?.purchases_brand_or_asin_price_median_percent,
    delta_accessor: (d) => d?.purchases_brand_or_asin_price_median_delta,
    format: "currency",
    axis: PRICE_AXIS,
    grouping: "merchandising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "purchases_brand_or_asin_price_median_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "purchases_brand_or_asin_price_median_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "purchases_brand_or_asin_price_median_delta",
      },
    ],
  },
  {
    id: "purchases_brand_or_asin_price_median_percent",
    color: metricColors.metric2[1],
    accessor: (d) => d?.purchases_brand_or_asin_price_median_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "purchases_same_day_shipping_speed",
    defaultCompare: "percent",
    color: metricColors.metric2[1],
    accessor: (d) => d?.purchases_same_day_shipping_speed,
    prev_accessor: (d) => d?.purchases_same_day_shipping_speed_prev,
    percent_accessor: (d) => d?.purchases_same_day_shipping_speed_percent,
    delta_accessor: (d) => d?.purchases_same_day_shipping_speed_delta,
    format: "number",
    axis: PURCHASES_AXIS,
    grouping: "merchandising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "purchases_same_day_shipping_speed_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "purchases_same_day_shipping_speed_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "purchases_same_day_shipping_speed_delta",
      },
    ],
  },
  {
    id: "purchases_same_day_shipping_speed_percent",
    color: metricColors.metric2[1],
    accessor: (d) => d?.purchases_same_day_shipping_speed_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "purchases_1d_shipping_speed",
    defaultCompare: "percent",
    color: metricColors.metric4[3],
    accessor: (d) => d?.purchases_1d_shipping_speed,
    prev_accessor: (d) => d?.purchases_1d_shipping_speed_prev,
    percent_accessor: (d) => d?.purchases_1d_shipping_speed_percent,
    delta_accessor: (d) => d?.purchases_1d_shipping_speed_delta,
    format: "number",
    chartType: "column",
    axis: PURCHASES_AXIS,
    grouping: "merchandising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "purchases_1d_shipping_speed_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "purchases_1d_shipping_speed_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "purchases_1d_shipping_speed_delta",
      },
    ],
  },
  {
    id: "purchases_1d_shipping_speed_percent",
    color: metricColors.metric2[1],
    accessor: (d) => d?.purchases_1d_shipping_speed_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "purchases_2d_shipping_speed",
    defaultCompare: "percent",
    color: metricColors.metric4[4],
    accessor: (d) => d?.purchases_2d_shipping_speed,
    prev_accessor: (d) => d?.purchases_2d_shipping_speed_prev,
    percent_accessor: (d) => d?.purchases_2d_shipping_speed_percent,
    delta_accessor: (d) => d?.purchases_2d_shipping_speed_delta,
    format: "number",
    chartType: "column",
    axis: PURCHASES_AXIS,
    grouping: "merchandising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "purchases_2d_shipping_speed_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "purchases_2d_shipping_speed_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "purchases_2d_shipping_speed_delta",
      },
    ],
  },
  {
    id: "purchases_2d_shipping_speed_percent",
    color: metricColors.metric2[1],
    accessor: (d) => d?.purchases_2d_shipping_speed_percent,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "purchases_same_day_shipping_speed_percentage",
    defaultCompare: "percent",
    color: metricColors.metric2[1],
    accessor: (d) => d?.purchases_same_day_shipping_speed_percentage,
    prev_accessor: (d) => d?.purchases_same_day_shipping_speed_percentage_prev,
    percent_accessor: (d) => d?.purchases_same_day_shipping_speed_percentage_percent,
    delta_accessor: (d) => d?.purchases_same_day_shipping_speed_percentage_delta,
    format: "percent",
    axis: PERCENT_AXIS,
    grouping: "merchandising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "purchases_same_day_shipping_speed_percentage_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "purchases_same_day_shipping_speed_percentage_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "purchases_same_day_shipping_speed_percentage_delta",
      },
    ],
  },
  {
    id: "purchases_same_day_shipping_speed_percentage_delta",
    color: metricColors.metric2[1],
    accessor: (d) => d?.purchases_same_day_shipping_speed_percentage_delta,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "purchases_1d_shipping_speed_percentage",
    defaultCompare: "percent",
    color: metricColors.metric4[3],
    accessor: (d) => d?.purchases_1d_shipping_speed_percentage,
    prev_accessor: (d) => d?.purchases_1d_shipping_speed_percentage_prev,
    percent_accessor: (d) => d?.purchases_1d_shipping_speed_percentage_percent,
    delta_accessor: (d) => d?.purchases_1d_shipping_speed_percentage_delta,
    format: "percent",
    axis: PERCENT_AXIS,
    grouping: "merchandising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "purchases_1d_shipping_speed_percentage_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "purchases_1d_shipping_speed_percentage_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "purchases_1d_shipping_speed_percentage_delta",
      },
    ],
  },
  {
    id: "purchases_1d_shipping_speed_percentage_delta",
    color: metricColors.metric4[3],
    accessor: (d) => d?.purchases_1d_shipping_speed_percentage_delta,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "purchases_2d_shipping_speed_percentage",
    defaultCompare: "percent",
    color: metricColors.metric4[4],
    accessor: (d) => d?.purchases_2d_shipping_speed_percentage,
    prev_accessor: (d) => d?.purchases_2d_shipping_speed_percentage_prev,
    percent_accessor: (d) => d?.purchases_2d_shipping_speed_percentage_percent,
    delta_accessor: (d) => d?.purchases_2d_shipping_speed_percentage_delta,
    format: "percent",
    axis: PERCENT_AXIS,
    grouping: "merchandising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "purchases_2d_shipping_speed_percentage_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "purchases_2d_shipping_speed_percentage_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "purchases_2d_shipping_speed_percentage_delta",
      },
    ],
  },
  {
    id: "purchases_2d_shipping_speed_percentage_delta",
    color: metricColors.metric4[4],
    accessor: (d) => d?.purchases_2d_shipping_speed_percentage_delta,
    format: "percent",
    axis: PERCENT_AXIS,
  },
  {
    id: "ctr_total",
    defaultCompare: "delta",
    color: metricColors.metric2[1],
    accessor: (d) => d?.ctr_total,
    prev_accessor: (d) => d?.ctr_total_prev,
    percent_accessor: (d) => d?.ctr_total_percent,
    delta_accessor: (d) => d?.ctr_total_delta,
    format: "percent",
    axis: PERCENT_AXIS,
    decimalPlaces: 2,
    grouping: "merchandising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "ctr_total_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "ctr_total_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "ctr_total_delta",
      },
    ],
  },
  {
    id: "ctr_total_delta",
    color: metricColors.metric2[1],
    accessor: (d) => d?.ctr_total_delta,
    format: "percent",
    axis: PERCENT_AXIS,
    decimalPlaces: 2,
    grouping: "merchandising",
  },
  {
    id: "ctr_brand_or_asin",
    defaultCompare: "delta",
    color: metricColors.metric2[1],
    accessor: (d) => d?.ctr_brand_or_asin,
    prev_accessor: (d) => d?.ctr_brand_or_asin_prev,
    percent_accessor: (d) => d?.ctr_brand_or_asin_percent,
    delta_accessor: (d) => d?.ctr_brand_or_asin_delta,
    format: "percent",
    axis: PERCENT_AXIS,
    decimalPlaces: 2,
    grouping: "merchandising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "ctr_brand_or_asin_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "ctr_brand_or_asin_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "ctr_brand_or_asin_delta",
      },
    ],
  },
  {
    id: "ctr_brand_or_asin_delta",
    color: metricColors.metric2[1],
    accessor: (d) => d?.ctr_brand_or_asin_delta,
    format: "percent",
    axis: PERCENT_AXIS,
    decimalPlaces: 2,
  },
  {
    id: "ctr_difference",
    color: metricColors.metric2[1],
    accessor: (d) => d?.ctr_difference,
    format: "percent",
    axis: PERCENT_AXIS,
    grouping: "merchandising",
    decimalPlaces: 2,
  },
  {
    id: "cv_total",
    defaultCompare: "delta",
    color: metricColors.metric2[1],
    accessor: (d) => d?.cv_total,
    prev_accessor: (d) => d?.cv_total_prev,
    percent_accessor: (d) => d?.cv_total_percent,
    delta_accessor: (d) => d?.cv_total_delta,
    format: "percent",
    axis: PERCENT_AXIS,
    decimalPlaces: 2,
    grouping: "merchandising",
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "cv_total_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "cv_total_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "cv_total_delta",
      },
    ],
  },
  {
    id: "cv_total_delta",
    color: metricColors.metric2[1],
    accessor: (d) => d?.cv_total_delta,
    format: "percent",
    decimalPlaces: 2,
    axis: PERCENT_AXIS,
  },
  {
    id: "cv_brand_or_asin",
    defaultCompare: "delta",
    color: metricColors.metric2[1],
    accessor: (d) => d?.cv_brand_or_asin,
    prev_accessor: (d) => d?.cv_brand_or_asin_prev,
    percent_accessor: (d) => d?.cv_brand_or_asin_percent,
    delta_accessor: (d) => d?.cv_brand_or_asin_delta,
    format: "percent",
    axis: PERCENT_AXIS,
    decimalPlaces: 2,
    grouping: "merchandising",
  },
  {
    id: "cv_brand_or_asin_delta",
    color: metricColors.metric2[1],
    accessor: (d) => d?.cv_brand_or_asin_delta,
    format: "percent",
    decimalPlaces: 2,
    axis: PERCENT_AXIS,
  },
  {
    id: "cv_difference",
    color: metricColors.metric2[1],
    accessor: (d) => d?.cv_difference,
    format: "percent",
    axis: PERCENT_AXIS,
    grouping: "merchandising",
    decimalPlaces: 2,
  },
  {
    id: "taov",
    defaultCompare: "percent",
    color: metricColors.sales[0],
    accessor: (d) => d?.taov ?? 0,
    prev_accessor: (d) => d?.taov_prev ?? 0,
    percent_accessor: (d) => d?.taov_percent ?? 0,
    delta_accessor: (d) => d?.taov_delta ?? 0,
    format: "currency",
    chartType: "line",
    axis: SALES_AXIS,
    grouping: "merchandising",
    decimalPlaces: 2,
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "taov_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "taov_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "taov_delta",
      },
    ],
  },
  {
    id: "tauv",
    defaultCompare: "percent",
    color: metricColors.sales[1],
    accessor: (d) => d?.tauv ?? 0,
    prev_accessor: (d) => d?.tauv_prev ?? 0,
    percent_accessor: (d) => d?.tauv_percent ?? 0,
    delta_accessor: (d) => d?.tauv_delta ?? 0,
    format: "currency",
    chartType: "line",
    axis: SALES_AXIS,
    grouping: "merchandising",
    decimalPlaces: 2,
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "tauv_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "tauv_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "tauv_delta",
      },
    ],
  },
  {
    id: "aaov",
    defaultCompare: "percent",
    color: metricColors.sales[3],
    accessor: (d) => d?.aaov ?? 0,
    prev_accessor: (d) => d?.aaov_prev ?? 0,
    percent_accessor: (d) => d?.aaov_percent ?? 0,
    delta_accessor: (d) => d?.aaov_delta ?? 0,
    format: "currency",
    chartType: "line",
    axis: SALES_AXIS,
    grouping: "advertising",
    decimalPlaces: 2,
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "aaov_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "aaov_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "aaov_delta",
      },
    ],
  },
  {
    id: "top_of_search_impression_share",
    defaultCompare: "delta",
    color: metricColors.sales[3],
    accessor: (d) => d?.top_of_search_impression_share ?? 0,
    prev_accessor: (d) => d?.top_of_search_impression_share_prev ?? 0,
    percent_accessor: (d) => d?.top_of_search_impression_share_percent ?? 0,
    delta_accessor: (d) => d?.top_of_search_impression_share_delta ?? 0,
    format: "percent",
    chartType: "line",
    axis: SALES_AXIS,
    grouping: "advertising",
    decimalPlaces: 2,
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "top_of_search_impression_share_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "top_of_search_impression_share_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "top_of_search_impression_share_delta",
      },
    ],
  },
  {
    id: "aauv",
    defaultCompare: "percent",
    color: metricColors.sales[4],
    accessor: (d) => d?.aauv ?? 0,
    prev_accessor: (d) => d?.aauv_prev ?? 0,
    percent_accessor: (d) => d?.aauv_percent ?? 0,
    delta_accessor: (d) => d?.aauv_delta ?? 0,
    format: "currency",
    chartType: "line",
    axis: SALES_AXIS,
    grouping: "advertising",
    decimalPlaces: 2,
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "aauv_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "aauv_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "aauv_delta",
      },
    ],
  },
  {
    id: "ssaov",
    defaultCompare: "percent",
    color: metricColors.metric1[1],
    accessor: (d) => d?.ssaov ?? 0,
    prev_accessor: (d) => d?.ssaov_prev ?? 0,
    percent_accessor: (d) => d?.ssaov_percent ?? 0,
    delta_accessor: (d) => d?.ssaov_delta ?? 0,
    format: "currency",
    chartType: "line",
    axis: SALES_AXIS,
    grouping: "advertising",
    decimalPlaces: 2,
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "ssaov_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "ssaov_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "ssaov_delta",
      },
    ],
  },
  {
    id: "ssauv",
    defaultCompare: "percent",
    color: metricColors.metric1[2],
    accessor: (d) => d?.ssauv ?? 0,
    prev_accessor: (d) => d?.ssauv_prev ?? 0,
    percent_accessor: (d) => d?.ssauv_percent ?? 0,
    delta_accessor: (d) => d?.ssauv_delta ?? 0,
    format: "currency",
    chartType: "line",
    axis: SALES_AXIS,
    grouping: "advertising",
    decimalPlaces: 2,
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "ssauv_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "ssauv_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "ssauv_delta",
      },
    ],
  },
  {
    id: "average_daily_spend",
    defaultCompare: "percent",
    color: metricColors.cost[1],
    accessor: (d) => d?.average_daily_spend ?? 0,
    prev_accessor: (d) => d?.average_daily_spend_prev ?? 0,
    percent_accessor: (d) => d?.average_daily_spend_percent ?? 0,
    delta_accessor: (d) => d?.average_daily_spend_delta ?? 0,
    format: "currency",
    chartType: "line",
    axis: SALES_AXIS,
    grouping: "advertising",
    decimalPlaces: 2,
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "average_daily_spend_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "average_daily_spend_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "average_daily_spend_delta",
      },
    ],
  },
  {
    id: "average_daily_sales",
    defaultCompare: "percent",
    color: metricColors.sales[2],
    accessor: (d) => d?.average_daily_sales ?? 0,
    prev_accessor: (d) => d?.average_daily_sales_prev ?? 0,
    percent_accessor: (d) => d?.average_daily_sales_percent ?? 0,
    delta_accessor: (d) => d?.average_daily_sales_delta ?? 0,
    format: "currency",
    chartType: "line",
    axis: SALES_AXIS,
    grouping: "advertising",
    decimalPlaces: 2,
    alternateSortingOptions: [
      {
        label: ($) => $.metrics["Compared Value"],
        value: "average_daily_sales_prev",
      },
      {
        label: ($) => $.metrics["% Change"],
        value: "average_daily_sales_percent",
      },
      {
        label: ($) => $.metrics["Absolute Change"],
        value: "average_daily_sales_delta",
      },
    ],
  },
  {
    id: "day_range",
    color: metricColors.metric2[0],
    accessor: (d) => d?.day_range,
    format: "number",
    axis: DAYS_AXIS,
    grouping: "configuration",
    decimalPlaces: 0,
  },
  {
    id: "adv_keyword_ids_count",
    color: metricColors.metric1[0],
    accessor: (d) => d?.adv_keyword_ids_count ?? null,
    format: "number",
    grouping: "configuration",
  },
  {
    id: "adv_target_ids_count",
    color: metricColors.metric1[0],
    accessor: (d) => d?.adv_target_ids_count ?? null,
    format: "number",
    grouping: "configuration",
  },

  {
    id: "daily_sales_goal",
    serverId: "avg_daily_sales_goal",
    color: metricColors.sales[0],
    accessor: (d) => d?.avg_daily_sales_goal ?? null,
    defaultCompare: "delta",
    prev_accessor: (d) => d?.avg_daily_sales_goal_prev ?? null,
    delta_accessor: goalMetricDeltaAccessor(
      (d) => d?.avg_daily_sales_goal ?? null,
      (d) => d?.avg_daily_sales_goal_prev ?? null,
    ),
    percent_accessor: goalMetricPercentAccessor(
      (d) => d?.avg_daily_sales_goal ?? null,
      (d) => d?.avg_daily_sales_goal_prev ?? null,
    ),
    goal_coverage_accessor: (d) => ({ withGoal: d?.days_with_goal ?? 0, total: d?.day_range ?? 0 }),
    format: "currency_goal",
    axis: SALES_AXIS,
    grouping: "merchandising",
    decimalPlaces: 2,
    compareHighlight: "neutral",
    sortNullsLast: true,
  },
  {
    id: "total_sales_pacing_percent",
    color: metricColors.sales[0],
    accessor: (d) => d?.total_sales_pacing_percent ?? null,
    defaultCompare: "delta",
    prev_accessor: (d) => d?.total_sales_pacing_percent_prev ?? null,
    delta_accessor: goalMetricDeltaAccessor(
      (d) => d?.total_sales_pacing_percent ?? null,
      (d) => d?.total_sales_pacing_percent_prev ?? null,
    ),
    percent_accessor: goalMetricPercentAccessor(
      (d) => d?.total_sales_pacing_percent ?? null,
      (d) => d?.total_sales_pacing_percent_prev ?? null,
    ),
    goal_coverage_accessor: (d) => ({ withGoal: d?.days_with_goal ?? 0, total: d?.day_range ?? 0 }),
    format: "percent_goal",
    axis: PERCENT_AXIS,
    grouping: "merchandising",
    decimalPlaces: 1,
    sortNullsLast: true,
  },
  {
    id: "total_sales_pacing_offset",
    color: metricColors.sales[0],
    accessor: (d) => d?.total_sales_pacing_offset ?? null,
    defaultCompare: "delta",
    prev_accessor: (d) => d?.total_sales_pacing_offset_prev ?? null,
    delta_accessor: goalMetricDeltaAccessor(
      (d) => d?.total_sales_pacing_offset ?? null,
      (d) => d?.total_sales_pacing_offset_prev ?? null,
    ),
    percent_accessor: goalMetricPercentAccessor(
      (d) => d?.total_sales_pacing_offset ?? null,
      (d) => d?.total_sales_pacing_offset_prev ?? null,
    ),
    goal_coverage_accessor: (d) => ({ withGoal: d?.days_with_goal ?? 0, total: d?.day_range ?? 0 }),
    format: "currency_goal",
    axis: SALES_AXIS,
    grouping: "merchandising",
    decimalPlaces: 2,
    incremental: true,
    sortNullsLast: true,
  },
  {
    id: "daily_ad_spend_goal",
    serverId: "avg_daily_ad_spend_goal",
    color: metricColors.cost[1],
    accessor: (d) => d?.avg_daily_ad_spend_goal ?? null,
    defaultCompare: "delta",
    prev_accessor: (d) => d?.avg_daily_ad_spend_goal_prev ?? null,
    delta_accessor: goalMetricDeltaAccessor(
      (d) => d?.avg_daily_ad_spend_goal ?? null,
      (d) => d?.avg_daily_ad_spend_goal_prev ?? null,
    ),
    percent_accessor: goalMetricPercentAccessor(
      (d) => d?.avg_daily_ad_spend_goal ?? null,
      (d) => d?.avg_daily_ad_spend_goal_prev ?? null,
    ),
    goal_coverage_accessor: (d) => ({ withGoal: d?.days_with_goal ?? 0, total: d?.day_range ?? 0 }),
    format: "currency_goal",
    axis: SALES_AXIS,
    grouping: "advertising",
    decimalPlaces: 2,
    compareHighlight: "neutral",
    sortNullsLast: true,
  },
  {
    id: "ad_spend_pacing_percent",
    color: metricColors.cost[1],
    accessor: (d) => d?.ad_spend_pacing_percent ?? null,
    defaultCompare: "delta",
    prev_accessor: (d) => d?.ad_spend_pacing_percent_prev ?? null,
    delta_accessor: goalMetricDeltaAccessor(
      (d) => d?.ad_spend_pacing_percent ?? null,
      (d) => d?.ad_spend_pacing_percent_prev ?? null,
    ),
    percent_accessor: goalMetricPercentAccessor(
      (d) => d?.ad_spend_pacing_percent ?? null,
      (d) => d?.ad_spend_pacing_percent_prev ?? null,
    ),
    goal_coverage_accessor: (d) => ({ withGoal: d?.days_with_goal ?? 0, total: d?.day_range ?? 0 }),
    format: "percent_goal",
    axis: PERCENT_AXIS,
    grouping: "advertising",
    decimalPlaces: 1,
    sortNullsLast: true,
    compareHighlight: "neutral",
  },
  {
    id: "ad_spend_pacing_offset",
    color: metricColors.cost[1],
    accessor: (d) => d?.ad_spend_pacing_offset ?? null,
    defaultCompare: "delta",
    prev_accessor: (d) => d?.ad_spend_pacing_offset_prev ?? null,
    delta_accessor: goalMetricDeltaAccessor(
      (d) => d?.ad_spend_pacing_offset ?? null,
      (d) => d?.ad_spend_pacing_offset_prev ?? null,
    ),
    percent_accessor: goalMetricPercentAccessor(
      (d) => d?.ad_spend_pacing_offset ?? null,
      (d) => d?.ad_spend_pacing_offset_prev ?? null,
    ),
    goal_coverage_accessor: (d) => ({ withGoal: d?.days_with_goal ?? 0, total: d?.day_range ?? 0 }),
    format: "currency_goal",
    axis: SALES_AXIS,
    grouping: "advertising",
    decimalPlaces: 2,
    incremental: true,
    sortNullsLast: true,
    compareHighlight: "neutral",
  },
  {
    id: "total_acos_goal",
    color: metricColors.acos[1],
    accessor: (d) => (d?.days_with_goal ? (d.total_acos_goal ?? null) : null),
    defaultCompare: "delta",
    prev_accessor: (d) => (d?.days_with_goal_prev ? (d.total_acos_goal_prev ?? null) : null),
    delta_accessor: goalMetricDeltaAccessor(
      (d) => (d?.days_with_goal ? (d.total_acos_goal ?? null) : null),
      (d) => (d?.days_with_goal_prev ? (d.total_acos_goal_prev ?? null) : null),
    ),
    percent_accessor: goalMetricPercentAccessor(
      (d) => (d?.days_with_goal ? (d.total_acos_goal ?? null) : null),
      (d) => (d?.days_with_goal_prev ? (d.total_acos_goal_prev ?? null) : null),
    ),
    goal_coverage_accessor: (d) => ({ withGoal: d?.days_with_goal ?? 0, total: d?.day_range ?? 0 }),
    format: "percent_goal",
    axis: PERCENT_AXIS,
    grouping: "merchandising",
    decimalPlaces: 1,
    compareHighlight: "neutral",
    sortNullsLast: true,
  },
  {
    id: "tacos_pacing_percent",
    color: metricColors.acos[1],
    accessor: (d) => d?.tacos_pacing_percent ?? null,
    defaultCompare: "delta",
    prev_accessor: (d) => d?.tacos_pacing_percent_prev ?? null,
    delta_accessor: goalMetricDeltaAccessor(
      (d) => d?.tacos_pacing_percent ?? null,
      (d) => d?.tacos_pacing_percent_prev ?? null,
    ),
    percent_accessor: goalMetricPercentAccessor(
      (d) => d?.tacos_pacing_percent ?? null,
      (d) => d?.tacos_pacing_percent_prev ?? null,
    ),
    goal_coverage_accessor: (d) => ({ withGoal: d?.days_with_goal ?? 0, total: d?.day_range ?? 0 }),
    format: "percent_goal",
    axis: PERCENT_AXIS,
    grouping: "merchandising",
    decimalPlaces: 1,
    lower: true,
    compareHighlight: "negativeIsGood",
    sortNullsLast: true,
  },
  {
    id: "tacos_pacing_offset",
    color: metricColors.acos[1],
    accessor: (d) => d?.tacos_pacing_offset ?? null,
    defaultCompare: "delta",
    prev_accessor: (d) => d?.tacos_pacing_offset_prev ?? null,
    delta_accessor: goalMetricDeltaAccessor(
      (d) => d?.tacos_pacing_offset ?? null,
      (d) => d?.tacos_pacing_offset_prev ?? null,
    ),
    percent_accessor: goalMetricPercentAccessor(
      (d) => d?.tacos_pacing_offset ?? null,
      (d) => d?.tacos_pacing_offset_prev ?? null,
    ),
    goal_coverage_accessor: (d) => ({ withGoal: d?.days_with_goal ?? 0, total: d?.day_range ?? 0 }),
    format: "percent_goal",
    axis: PERCENT_AXIS,
    grouping: "merchandising",
    decimalPlaces: 1,
    incremental: true,
    lower: true,
    compareHighlight: "negativeIsGood",
    sortNullsLast: true,
  },
];

export const getFormatter = (
  marketPlace: { marketPlace?: string } | undefined | null,
  format: string,
  metric?: { decimalPlaces?: number; incremental?: boolean; compact?: boolean },
  options?: { overrideFormatter?: (v: unknown) => string; incremental?: boolean },
): ((v: unknown) => ReactNode) => {
  const defaultFormatter = (v: unknown) => (v == null ? "-" : (v as string));
  const decimalPlaces = metric?.decimalPlaces ?? 1;

  const baseFormat =
    format === "percent_goal" ? "percent"
    : format === "currency_goal" ? "currency"
    : format;

  let compact = false;
  if ((baseFormat === "currency" && metric?.compact !== false) || metric?.compact) {
    compact = true;
  }

  const formatter =
    {
      currency: (v: unknown) =>
        v == null ? "-" : (
          formatCurrency(v as number, {
            marketPlace: marketPlace?.marketPlace,
            decimalPlaces: metric?.decimalPlaces ?? 2,
            compact: compact,
          })
        ),
      number: (v: unknown) =>
        v == null || isNaN(v as number) ? "-" : formatNumber(v as number, { decimalPlaces, compact }),
      percent: (v: unknown) =>
        v == null || isNaN(v as number) ?
          "-"
        : formatPercent(v as number, {
            decimalPlaces: decimalPlaces,
          }),
      text: (v: unknown) => {
        return v == null ? "-" : (v as string);
      },
    }[baseFormat] ?? defaultFormatter;

  if (options?.overrideFormatter) {
    return (v: unknown) => options.overrideFormatter?.(v) ?? "-";
  }
  if (options?.incremental ?? metric?.incremental) {
    return (v: unknown) => ((v as number) > 0 ? "+" : "") + formatter(v);
  }
  return formatter;
};

const allMetrics = [...dspMetrics, ...metrics];

if (import.meta.env.DEV) {
  const seenIds = new Set<string>();
  for (const m of allMetrics) {
    if (seenIds.has(m.id)) {
      throw new Error(`Duplicate metric id "${m.id}" — all registered metrics must have unique ids`);
    }
    seenIds.add(m.id);
  }
}

const metricById = new Map(allMetrics.map((m) => [m.id, m]));

export const isKnownMetric = (id: string): boolean => metricById.has(id);

const getMetricSpec = <T,>(id: string) => {
  return (
    (metricById.get(id) as MetricConfig<T> | undefined) ??
    ({
      id,
      format: "text",
      accessor: (d) => (d?.[id as keyof T] as number | undefined) ?? null,
    } satisfies MetricWithoutCompare<T>)
  );
};

export default getMetricSpec;

const serverIdMap = new Map(allMetrics.filter((m) => m.serverId).map((m) => [m.id, m.serverId!] as const));

export function resolveServerId(columnId: string): string {
  return serverIdMap.get(columnId) ?? columnId;
}
